import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import MenuSetManager from '../../components/MenuSetManager';
import useAuthStore from '../../store/authStore';
import { getPlanLimit, hasPlanFeature } from '../../utils/planAccess';
import UpgradeModal from '../../components/UpgradeModal';
import ImageCropModal from '../../components/ImageCropModal';
import MenuPrintModal from '../../components/MenuPrintModal';
import { confirm } from '../../components/ConfirmDialog';

// Fallback icon map for known printer names
const PRINTER_ICON_MAP = {
    'xp-kassa':  '🖨️',
    'xp-metbex': '🔥',
    'xp-soyuq':  '❄️',
    'xp-barmen': '🍹',
};
const getPrinterIcon = (name) => PRINTER_ICON_MAP[name] || '🖨️';
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const IMAGE_OPTIMIZE_THRESHOLD_BYTES = 350 * 1024;
const IMAGE_MAX_DIMENSION = 1600;

const getAssetBaseUrl = () => process.env.REACT_APP_API_URL?.replace('/api', '') || window.location.origin;

const loadImageElement = (file) => new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(image);
    };
    image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Şəkil oxunmadı.'));
    };
    image.src = objectUrl;
});

const canvasToBlob = (canvas, type, quality) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
        if (!blob) {
            reject(new Error('Şəkil hazırlamaq mümkün olmadı.'));
            return;
        }

        resolve(blob);
    }, type, quality);
});

const optimizeUploadImage = async (file) => {
    try {
        const fileName = String(file?.name || '').toLowerCase();
        const isPng = file.type === 'image/png' || fileName.endsWith('.png');
        const shouldOptimize = file.size > IMAGE_OPTIMIZE_THRESHOLD_BYTES || isPng;

        if (!shouldOptimize) {
            return file;
        }

        const image = await loadImageElement(file);
        const width = image.width || 0;
        const height = image.height || 0;
        const largestSide = Math.max(width, height);

        let targetWidth = width;
        let targetHeight = height;

        if (largestSide > IMAGE_MAX_DIMENSION && largestSide > 0) {
            const ratio = IMAGE_MAX_DIMENSION / largestSide;
            targetWidth = Math.max(1, Math.round(width * ratio));
            targetHeight = Math.max(1, Math.round(height * ratio));
        }

        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        const context = canvas.getContext('2d', { alpha: isPng });
        if (!context) {
            return file;
        }

        if (!isPng) {
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, targetWidth, targetHeight);
        }

        context.drawImage(image, 0, 0, targetWidth, targetHeight);

        const outputType = isPng ? 'image/png' : 'image/jpeg';
        const blob = await canvasToBlob(canvas, outputType, isPng ? undefined : 0.82);

        if (blob.size >= file.size) {
            return file;
        }

        const outputName = isPng
            ? file.name.replace(/\.[^.]+$/i, '.png')
            : file.name.replace(/\.[^.]+$/i, '.jpg');

        return new File([blob], outputName, {
            type: outputType,
            lastModified: Date.now()
        });
    } catch {
        return file;
    }
};

const NOVELTIES = 'novelties'; // «✨ Yeniliklər» virtual kateqoriyasının id-si

const Menu = ({ embedded = false }) => {
    const { user } = useAuthStore();
    const navigate = useNavigate();
    const canUseMenuImages = hasPlanFeature(user?.tenant_plan, 'menu_images');
    const maxMenuImages = getPlanLimit(user?.tenant_plan, 'maxMenuImages');
    const canUseRecipes = hasPlanFeature(user?.tenant_plan, 'recipes');
    // Menyu məhsulundan birbaşa texkartasına keç (Texkarta tab + həmin məhsul seçili)
    const openTexkarta = (item) => navigate(`/warehouse/dashboard?tab=recipe&item=${item.id}`);
    const [categories, setCategories] = useState([]);
    const [activeTab, setActiveTab] = useState('menu');
    const [items, setItems]           = useState([]);
    const [loading, setLoading]       = useState(true);
    const [activeCat, setActiveCat]   = useState(null); // null = "Hamısı" (default)
    const [itemSearch, setItemSearch] = useState('');
    const [lowMarginFilter, setLowMarginFilter] = useState(false); // maya ≥/yaxın qiymət filtri
    const [marginPct, setMarginPct] = useState(15); // minimum mənfəət faizi (default 15%)
    const [noImageFilter, setNoImageFilter] = useState(false); // yalnız şəkilsiz məhsullar
    const [staffOnlyFilter, setStaffOnlyFilter] = useState(false); // yalnız «🔒 Yalnız admin/kassir» məhsullar
    const [setOnlyFilter, setSetOnlyFilter] = useState(false);     // yalnız «🧩 Yalnız set/banket içində» məhsullar
    const [filtersOpen, setFiltersOpen] = useState(false); // «Filtrlər» paneli açıq?
    const noveltyVisibleCountRef = useRef(0);
    const [printModalOpen, setPrintModalOpen] = useState(false); // menyu çapı / PDF modalı

    const [catModal, setCatModal]   = useState(false);
    const [itemModal, setItemModal] = useState(false);
    const [itemTab, setItemTab]     = useState('main'); // məhsul modalı tabı: main | image | variants | access
    const [preparedImgFile, setPreparedImgFile] = useState(null); // əvvəlcədən hazırlanmış JPEG (share user-gesture itməsin)
    const [catSheetOpen, setCatSheetOpen] = useState(false); // mobil: kateqoriya paneli (bottom-sheet)
    const [catSearch, setCatSearch] = useState('');     // kateqoriya axtarış comboboxu
    const [catOpen, setCatOpen]     = useState(false);   // kateqoriya dropdown açıq?
    const [editCat, setEditCat]     = useState(null);
    const [editItem, setEditItem]   = useState(null);

    const [catForm, setCatForm] = useState({
        name: '', sort_order: 0, printer: 'xp-metbex', is_open_price: false, staff_only: false
    });
    const [catTab, setCatTab] = useState('main'); // kateqoriya modalı tabı: main | printers | image
    const [pendingCatImage, setPendingCatImage] = useState(null);       // yeni kateqoriya üçün seçilmiş fayl
    const [pendingCatImageUrl, setPendingCatImageUrl] = useState(null); // və ya internetdən url
    const [pendingCatImagePreview, setPendingCatImagePreview] = useState(null);
    const [imageUploading, setImageUploading] = useState(false);
    const imageInputRef = useRef(null);
    const [itemImageUploading, setItemImageUploading] = useState(false);
    const [cropState, setCropState] = useState(null); // { file, target: 'edit' | 'new' } — kəsmə pəncərəsi
    const itemImageInputRef = useRef(null);
    const [pendingItemImage, setPendingItemImage] = useState(null);
    const [pendingItemImagePreview, setPendingItemImagePreview] = useState(null);
    const [imgResults, setImgResults]   = useState(null);   // internet şəkil axtarışı nəticələri
    const [imgSearching, setImgSearching] = useState(false);
    const [imgPage, setImgPage]         = useState(1);
    const [imgHasMore, setImgHasMore]   = useState(false);
    const [imgQuery, setImgQuery]       = useState('');
    const [imgQueryEdit, setImgQueryEdit] = useState(''); // redaktə oluna bilən axtarış sözü
    const [imgQueryDirty, setImgQueryDirty] = useState(false); // istifadəçi əl ilə dəyişibmi
    const [pendingImageUrl, setPendingImageUrl] = useState(null); // yeni məhsul üçün seçilmiş internet şəkli
    const newItemImageInputRef = useRef(null);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [manualCost, setManualCost] = useState(false);
    const [itemForm, setItemForm] = useState({
        category_id: '', name: '', description: '', price: '', sale_type: 'piece', is_new: false, staff_only: false, set_only: false, banquet_role: '', cost_price: '', sort_order: 0, printer: null, qr_portion_grams: '', portion_pieces: 1, is_hourly: false
    });
    const [hourlyEnabled, setHourlyEnabled] = useState(false); // tenant üçün saatlıq menyu aktiv?

    // Printers (loaded from API)
    const [printers, setPrinters] = useState([]);

    // Variants state
    const [variants, setVariants]           = useState([]);
    const [variantForm, setVariantForm]     = useState({ name: '', price: '' });
    const [variantLoading, setVariantLoading] = useState(false);
    const variantNameRef  = useRef(null); // klaviatura axını: ad → qiymət → yeni variant
    const variantPriceRef = useRef(null);

    // Variant printer state (variantId -> printer name being set)
    const [variantPrinterEdit, setVariantPrinterEdit] = useState(null); // variantId currently open for printer select
    const [variantDragIndex, setVariantDragIndex] = useState(null); // sürüşdürmə (reorder) üçün

    useEffect(() => { loadAll(); }, []);
    useEffect(() => {
        let alive = true;
        api.get('/menu/hourly-enabled').then((r) => { if (alive) setHourlyEnabled(!!r.data?.hourly_menu_enabled); }).catch(() => {});
        return () => { alive = false; };
    }, []);
    // Menyuya girəndə default "Hamısı" seçili qalsın (activeCat = null) — avtomatik kateqoriya seçilmir.

    // Texkartadan "← Menyuya qayıt" → ?focus=<id>: həmin məhsulun kateqoriyasını seç + sətrə scroll + highlight
    const [highlightItemId, setHighlightItemId] = useState(null);
    // «✨ Yeniliklər» virtual kateqoriyası (is_new məhsullar): server sırası + əl sırası bayrağı
    const [noveltyIds, setNoveltyIds] = useState([]);
    const [noveltiesCustom, setNoveltiesCustom] = useState(false);
    const focusItemRef = useRef(null);
    useEffect(() => {
        const f = new URLSearchParams(window.location.search).get('focus');
        if (f) {
            focusItemRef.current = Number(f);
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []);
    useEffect(() => {
        if (!focusItemRef.current || items.length === 0) return;
        const targetId = focusItemRef.current;
        focusItemRef.current = null;
        const it = items.find((i) => i.id === targetId);
        if (!it) return;
        setActiveCat(it.category_id);
        setHighlightItemId(targetId);
        setTimeout(() => {
            const el = [`menu-item-${targetId}`, `menu-item-m-${targetId}`]
                .map((id) => document.getElementById(id))
                .find((e) => e && e.offsetParent !== null);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 180);
        setTimeout(() => setHighlightItemId(null), 3000);
    }, [items]);

    const loadAll = async () => {
        try {
            const [catRes, itemRes, printerRes] = await Promise.all([
                api.get('/menu/categories/all'),
                api.get('/menu/items/all'),
                api.get('/printers').catch(() => ({ data: { data: [] } }))
            ]);
            setCategories(catRes.data.data);
            setItems(itemRes.data.data);
            setNoveltyIds(itemRes.data.novelty_ids || []);
            setNoveltiesCustom(Number(itemRes.data.novelties_custom_sorted || 0) === 1);
            setManualCost(Number(itemRes.data.manual_cost_enabled || 0) === 1);
            setPrinters(printerRes.data.data || []);
        } catch {
            toast.error('Məlumat yüklənmədi.');
        } finally {
            setLoading(false);
        }
    };

    // ── KATEQORİYA ────────────────────────────────────────────
    const clearPendingCatImage = () => {
        if (pendingCatImagePreview && pendingCatImagePreview.startsWith('blob:')) URL.revokeObjectURL(pendingCatImagePreview);
        setPendingCatImage(null);
        setPendingCatImageUrl(null);
        setPendingCatImagePreview(null);
    };

    const openCatCreate = () => {
        setCatForm({ name: '', sort_order: categories.length + 1, printer: printers[0]?.name || null, is_open_price: false, staff_only: false });
        setEditCat(null);
        setImgResults(null);
        setImgQueryDirty(false);
        setCatTab('main');
        clearPendingCatImage();
        setCatModal(true);
    };

    const openCatEdit = (cat) => {
        setCatForm({
            name:       cat.name,
            sort_order: cat.sort_order,
            printer:    cat.printer || null,
            is_open_price: Boolean(Number(cat.is_open_price || 0)),
            staff_only: Boolean(Number(cat.staff_only || 0))
        });
        setEditCat(cat);
        setImgResults(null);
        setImgQueryDirty(false);
        setCatTab('main');
        clearPendingCatImage();
        setCatModal(true);
    };

    const handleCatSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editCat) {
                await api.put(`/menu/categories/${editCat.id}`, {
                    name:       catForm.name,
                    sort_order: catForm.sort_order,
                    printer:    catForm.printer,
                    is_open_price: catForm.is_open_price ? 1 : 0,
                    staff_only: catForm.staff_only ? 1 : 0,
                    active:     editCat.active
                });
                toast.success('Kateqoriya yeniləndi.');
            } else {
                const res = await api.post('/menu/categories', catForm);
                const newCatId = res.data?.id;
                // Yaradılmamışdan seçilmiş şəkli tətbiq et
                if (newCatId && pendingCatImage) {
                    const fd = new FormData();
                    fd.append('image', pendingCatImage, pendingCatImage.name);
                    try { await api.post(`/menu/categories/${newCatId}/image`, fd); }
                    catch { toast.error('Şəkil əlavə olunmadı, amma kateqoriya yaradıldı.'); }
                } else if (newCatId && pendingCatImageUrl) {
                    try { await api.post(`/menu/categories/${newCatId}/image-from-url`, { url: pendingCatImageUrl }); }
                    catch { toast.error('Şəkil əlavə olunmadı, amma kateqoriya yaradıldı.'); }
                }
                toast.success('Kateqoriya əlavə edildi.');
            }
            clearPendingCatImage();
            setCatModal(false);
            loadAll();
        } catch (err) {
            if (err.response?.status === 403) setShowUpgradeModal(true);
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const handleCatToggle = async (cat) => {
        try {
            await api.put(`/menu/categories/${cat.id}`, {
                name:       cat.name,
                sort_order: cat.sort_order,
                printer:    cat.printer,
                active:     cat.active ? 0 : 1
            });
            toast.success(cat.active ? 'Deaktiv edildi.' : 'Aktiv edildi.');
            loadAll();
        } catch {
            toast.error('Xəta baş verdi.');
        }
    };

    const handleCatDelete = async (cat) => {
        if (!(await confirm(`"${cat.name}" silinsin?`))) return;
        try {
            await api.delete(`/menu/categories/${cat.id}`);
            toast.success('Kateqoriya silindi.');
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinə bilmədi.');
        }
    };

    // ── Drag & drop reorder ─────────────────────────────────────
    const [dragId, setDragId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);   // üstünə gətirilən hədəf (buraxılış xətti)
    const handleDragStart = (id) => setDragId(id);
    const handleDragOver  = (e, id) => { e.preventDefault(); if (id !== undefined && id !== dragOverId) setDragOverId(id); };
    const handleDrop = async (targetId) => {
        setDragOverId(null);
        if (!dragId || dragId === targetId) { setDragId(null); return; }
        const oldIdx = categories.findIndex(c => c.id === dragId);
        const newIdx = categories.findIndex(c => c.id === targetId);
        if (oldIdx < 0 || newIdx < 0) { setDragId(null); return; }
        const reordered = [...categories];
        const [moved] = reordered.splice(oldIdx, 1);
        reordered.splice(newIdx, 0, moved);
        setCategories(reordered);     // optimistic UI
        setDragId(null);
        try {
            await api.post('/menu/categories/reorder', { ids: reordered.map(c => c.id) });
            toast.success('Sıra yeniləndi. QR menyu və banketdə də bu sıra ilə görünəcək.');
        } catch {
            toast.error('Sıra yenilənə bilmədi.');
            loadAll();
        }
    };

    // ▲▼ ilə sıralama — toxunma ekranında (telefon/planşet) da işləyir (drag işləmir)
    const moveCategory = async (catId, dir) => {
        const idx = categories.findIndex(c => c.id === catId);
        const swap = idx + dir;
        if (idx < 0 || swap < 0 || swap >= categories.length) return;
        const reordered = [...categories];
        [reordered[idx], reordered[swap]] = [reordered[swap], reordered[idx]];
        setCategories(reordered);   // optimistik
        try {
            await api.post('/menu/categories/reorder', { ids: reordered.map(c => c.id) });
            toast.success('Sıra yeniləndi. QR menyu və banketdə də bu sıra ilə görünəcək.');
        } catch {
            toast.error('Sıra yenilənə bilmədi.');
            loadAll();
        }
    };

    // Kateqoriya üçün internetdən şəkil axtar (redaktə oluna bilən söz ilə)
    const searchCategoryImage = () => loadImages(imgQueryEdit, 1);
    const pickCategoryImage = async (url) => {
        // Yeni kateqoriya → url-i pending saxla (yaradılanda tətbiq olunur)
        if (!editCat?.id) {
            clearPendingCatImage();
            setPendingCatImageUrl(url);
            setPendingCatImagePreview(url);
            setImgResults(null);
            return;
        }
        setImageUploading(true);
        try {
            const { data } = await api.post(`/menu/categories/${editCat.id}/image-from-url`, { url });
            setEditCat(prev => ({ ...prev, image_url: data.image_url }));
            setImgResults(null);
            toast.success('Şəkil əlavə olundu.');
        } catch (err) { toast.error(err.response?.data?.msg || 'Şəkil əlavə olunmadı.'); }
        finally { setImageUploading(false); }
    };

    const handleImageUpload = async (e) => {
        const rawFile = e.target.files[0];
        const file = rawFile ? await optimizeUploadImage(rawFile) : null;
        if (!file) return;
        if (!canUseMenuImages) {
            setShowUpgradeModal(true);
            if (imageInputRef.current) imageInputRef.current.value = '';
            return;
        }
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
            toast.error('Şəkil ölçüsü maksimum 5 MB ola bilər.');
            if (imageInputRef.current) imageInputRef.current.value = '';
            return;
        }
        // Yeni kateqoriya → faylı pending saxla + preview
        if (!editCat) {
            clearPendingCatImage();
            setPendingCatImage(file);
            setPendingCatImagePreview(URL.createObjectURL(file));
            if (imageInputRef.current) imageInputRef.current.value = '';
            return;
        }
        const formData = new FormData();
        formData.append('image', file, file.name);
        setImageUploading(true);
        try {
            const res = await api.post(`/menu/categories/${editCat.id}/image`, formData);
            toast.success('Şəkil yükləndi.');
            setEditCat(prev => ({ ...prev, image_url: res.data.image_url }));
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Şəkil yüklənmədi.');
        } finally {
            setImageUploading(false);
            if (imageInputRef.current) imageInputRef.current.value = '';
        }
    };

    const handleImageDelete = async () => {
        if (!editCat?.image_url) return;
        try {
            await api.delete(`/menu/categories/${editCat.id}/image`);
            toast.success('Şəkil silindi.');
            setEditCat(prev => ({ ...prev, image_url: null }));
            loadAll();
        } catch {
            toast.error('Xəta baş verdi.');
        }
    };

    // Hissələri birləşdir + təkrar sözləri təmizlə (məs. "Mango" + "Mango smoothies" → "Mango smoothies")
    const buildImgQuery = (parts) => {
        const words = parts.map(s => String(s || '').trim()).filter(Boolean).join(' ').split(/\s+/);
        const seen = new Set(); const out = [];
        for (const w of words) { const k = w.toLowerCase(); if (k && !seen.has(k)) { seen.add(k); out.push(w); } }
        return out.join(' ');
    };

    // Axtarış sözünü avtomatik ad + təsvir + KATEQORİYA adından qur — istifadəçi əl ilə dəyişməyibsə
    useEffect(() => {
        if (imgQueryDirty) return;
        if (itemModal) {
            const catName = categories.find(c => String(c.id) === String(itemForm.category_id))?.name || '';
            setImgQueryEdit(buildImgQuery([itemForm.name, itemForm.description, catName]));
        } else if (catModal) {
            setImgQueryEdit(String(catForm.name || '').trim());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemModal, catModal, itemForm.name, itemForm.description, itemForm.category_id, catForm.name, categories, imgQueryDirty]);

    // Şəkil axtarış sözünü İNGİLİSCƏYƏ çevir (ad çox vaxt azərbaycancadır → Pexels ingiliscə yaxşı tapır).
    // Debounce: yazı dayananda 700ms sonra tərcümə edib inputa yazır (istifadəçi əl ilə dəyişməyibsə).
    useEffect(() => {
        if (imgQueryDirty) return;
        const src = (itemModal
            ? [itemForm.name, itemForm.description]
            : catModal ? [catForm.name] : []
        ).map(s => String(s || '').trim()).filter(Boolean).join(' ').trim();
        if (src.length < 2) return;
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get('/menu/translate', { params: { q: src } });
                if (data?.text) setImgQueryEdit((prev) => (imgQueryDirty ? prev : data.text));
            } catch { /* tərcümə alınmadı — xam mətn qalır */ }
        }, 700);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemModal, catModal, itemForm.name, itemForm.description, catForm.name, imgQueryDirty]);

    // Ümumi şəkil yükləyici (səhifələmə ilə) — həm məhsul, həm kateqoriya işlədir
    const loadImages = async (q, page) => {
        q = String(q || '').trim();
        if (q.length < 2) { toast.error('Əvvəlcə adı yazın.'); return; }
        setImgSearching(true);
        if (page === 1) setImgResults(null);
        try {
            const { data } = await api.get('/menu/image-search', { params: { q, page } });
            setImgResults(data.results || []);
            setImgPage(data.page || page);
            setImgHasMore(!!data.has_more);
            setImgQuery(q);
            if (page === 1 && !data.results?.length) toast('Şəkil tapılmadı — adı dəyişib yenidən yoxlayın.', { icon: '🔍' });
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Axtarış alınmadı.');
        } finally { setImgSearching(false); }
    };

    // İnternetdən şəkil axtar (redaktə oluna bilən söz ilə)
    const searchProductImage = () => loadImages(imgQueryEdit, 1);

    // Səhifələmə düymələri (nəticələrin altında)
    const renderImgPager = () => (imgResults && (imgResults.length > 0 || imgPage > 1)) ? (
        <div className="mt-2 flex items-center justify-between text-xs">
            <button type="button" disabled={imgPage <= 1 || imgSearching} onClick={() => loadImages(imgQuery, imgPage - 1)}
                className="rounded-lg border border-gray-200 px-3 py-1 font-bold text-gray-600 transition-all hover:bg-gray-50 disabled:opacity-40">← Geri</button>
            <span className="text-gray-400">Səhifə {imgPage}</span>
            <button type="button" disabled={!imgHasMore || imgSearching} onClick={() => loadImages(imgQuery, imgPage + 1)}
                className="rounded-lg border border-gray-200 px-3 py-1 font-bold text-gray-600 transition-all hover:bg-gray-50 disabled:opacity-40">Növbəti →</button>
        </div>
    ) : null;

    // Seçilmiş internet şəklini tətbiq et (mövcud → dərhal; yeni → yaradılanda)
    const pickProductImage = async (url) => {
        if (editItem?.id) {
            setItemImageUploading(true);
            try {
                const { data } = await api.post(`/menu/items/${editItem.id}/image-from-url`, { url });
                setEditItem(prev => ({ ...prev, image_url: data.image_url }));
                setImgResults(null);
                toast.success('Şəkil əlavə olundu.');
            } catch (err) {
                toast.error(err.response?.data?.msg || 'Şəkil əlavə olunmadı.');
            } finally { setItemImageUploading(false); }
        } else {
            setPendingImageUrl(url);
            setPendingItemImage(null);
            if (pendingItemImagePreview) { URL.revokeObjectURL(pendingItemImagePreview); setPendingItemImagePreview(null); }
            setPendingItemImagePreview(url);   // önizləmə üçün birbaşa URL
            setImgResults(null);
            toast.success('Şəkil seçildi — məhsul əlavə olunanda tətbiq olunacaq.');
        }
    };

    // ── MƏHSUL ────────────────────────────────────────────────
    // Fayl seçildi → əvvəlcə kəsmə pəncərəsi (kvadrat), sonra yükləmə
    const handleItemImageUpload = (e) => {
        const rawFile = e.target.files[0];
        if (itemImageInputRef.current) itemImageInputRef.current.value = '';
        if (!rawFile || !editItem) return;
        if (!canUseMenuImages) { setShowUpgradeModal(true); return; }
        setCropState({ file: rawFile, target: 'edit' });
    };

    const uploadEditItemImage = async (rawFile) => {
        const file = rawFile ? await optimizeUploadImage(rawFile) : null;
        if (!file || !editItem) return;
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
            toast.error('Şəkil ölçüsü maksimum 5 MB ola bilər.');
            if (itemImageInputRef.current) itemImageInputRef.current.value = '';
            return;
        }
        const formData = new FormData();
        formData.append('image', file, file.name);
        setItemImageUploading(true);
        try {
            const res = await api.post(`/menu/items/${editItem.id}/image`, formData);
            toast.success('Məhsul şəkli yükləndi.');
            setEditItem(prev => ({ ...prev, image_url: res.data.image_url }));
            loadAll();
        } catch (err) {
            if (err.response?.status === 403) setShowUpgradeModal(true);
            const debugMsg = err.response?.data?.msg
                || `[${err.response?.status ?? 'NO_RESPONSE'}] ${err.message}`;
            toast.error(debugMsg, { duration: 10000 });
        } finally {
            setItemImageUploading(false);
            if (itemImageInputRef.current) itemImageInputRef.current.value = '';
        }
    };

    const handleItemImageDelete = async () => {
        if (!editItem?.image_url) return;
        try {
            await api.delete(`/menu/items/${editItem.id}/image`);
            toast.success('Məhsul şəkli silindi.');
            setEditItem(prev => ({ ...prev, image_url: null }));
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Məhsul şəkli silinmədi.');
        }
    };

    // Şəkli canvas ilə REAL JPEG-ə çevir — iPhone/Android "Şəkillərə yadda saxla" həmişə açılsın
    // (server yanlış content-type qaytarsa və ya webp olsa belə, çıxış təmiz image/jpeg olur).
    const blobToJpeg = (blob) => new Promise((resolve, reject) => {
        const img = new Image();
        const u = URL.createObjectURL(blob);
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth || img.width;
                canvas.height = img.naturalHeight || img.height;
                const ctx = canvas.getContext('2d');
                ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height); // şəffaf PNG üçün ağ fon
                ctx.drawImage(img, 0, 0);
                canvas.toBlob((b) => { URL.revokeObjectURL(u); b ? resolve(b) : reject(new Error('toBlob null')); }, 'image/jpeg', 0.92);
            } catch (e) { URL.revokeObjectURL(u); reject(e); }
        };
        img.onerror = () => { URL.revokeObjectURL(u); reject(new Error('img load')); };
        img.src = u;
    });

    // Məhsul şəklini endir. iOS-da navigator.share yalnız toxunuşun DƏRHAL ardınca işləyir —
    // ona görə JPEG faylı əvvəlcədən hazırlanır (aşağıdakı useEffect), burada dərhal paylaşılır.
    const handleItemImageDownload = async () => {
        if (!editItem?.image_url) return;
        const url = `${getAssetBaseUrl()}${editItem.image_url}`;
        const isMobile = /Android|iPhone|iPad|iPod|Mobi/i.test(navigator.userAgent || '') || (navigator.maxTouchPoints || 0) > 1;

        // Mobil + hazır fayl → DƏRHAL paylaş (gesture itməsin). iPhone-da "Şəkillərə yadda saxla".
        if (isMobile && preparedImgFile && navigator.canShare && navigator.canShare({ files: [preparedImgFile] })) {
            try { await navigator.share({ files: [preparedImgFile], title: preparedImgFile.name }); return; }
            catch (e) { if (e && e.name === 'AbortError') return; /* dəstəklənmirsə aşağı düş */ }
        }

        // Fayl hələ hazır deyil (mobil) → sinxron paylaşmaq mümkün deyil; istifadəçiyə bildir
        if (isMobile && navigator.share && !preparedImgFile) {
            toast('Şəkil hazırlanır, bir az sonra "Endir"ə bir də toxunun.', { icon: '⏳' });
        }

        // Desktop (və ya share yoxdursa) → blob download
        try {
            let outBlob = preparedImgFile, outName = preparedImgFile?.name;
            if (!outBlob) {
                const raw = await (await fetch(url)).blob();
                const safeName = (editItem.name || 'mehsul').replace(/[^\p{L}\p{N}_-]+/gu, '_').slice(0, 40) || 'mehsul';
                try { outBlob = await blobToJpeg(raw); } catch { outBlob = new Blob([raw], { type: 'image/jpeg' }); }
                outName = `${safeName}.jpg`;
            }
            const a = document.createElement('a');
            a.href = URL.createObjectURL(outBlob);
            a.download = outName;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(a.href), 1500);
        } catch {
            window.open(url, '_blank'); // son çarə: yeni tabda aç
        }
    };

    // Şəkli əvvəlcədən JPEG-ə çevirib hazır saxla ki, "Endir" toxunuşunda navigator.share dərhal işləsin
    useEffect(() => {
        let cancelled = false;
        setPreparedImgFile(null);
        if (!itemModal || !editItem?.image_url) return;
        (async () => {
            try {
                const raw = await (await fetch(`${getAssetBaseUrl()}${editItem.image_url}`)).blob();
                const safeName = (editItem.name || 'mehsul').replace(/[^\p{L}\p{N}_-]+/gu, '_').slice(0, 40) || 'mehsul';
                let jpeg;
                try { jpeg = await blobToJpeg(raw); } catch { jpeg = new Blob([raw], { type: 'image/jpeg' }); }
                if (cancelled) return;
                setPreparedImgFile(new File([jpeg], `${safeName}.jpg`, { type: 'image/jpeg' }));
            } catch { /* şəbəkə xətası — fallback download işləyəcək */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemModal, editItem?.image_url, editItem?.name]);

    const handlePendingItemImageSelect = (e) => {
        const rawFile = e.target.files[0];
        if (newItemImageInputRef.current) newItemImageInputRef.current.value = '';
        if (!rawFile) return;
        if (!canUseMenuImages) { setShowUpgradeModal(true); return; }
        setCropState({ file: rawFile, target: 'new' });
    };

    const setPendingItemImageFromFile = async (rawFile) => {
        const file = rawFile ? await optimizeUploadImage(rawFile) : null;
        if (!file) return;
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
            toast.error('Şəkil ölçüsü maksimum 5 MB ola bilər.');
            if (newItemImageInputRef.current) newItemImageInputRef.current.value = '';
            return;
        }
        if (pendingItemImagePreview) URL.revokeObjectURL(pendingItemImagePreview);
        setPendingItemImage(file);
        setPendingItemImagePreview(URL.createObjectURL(file));
        if (newItemImageInputRef.current) newItemImageInputRef.current.value = '';
    };

    const openItemCreate = () => {
        const defCatId = (activeCat && activeCat !== NOVELTIES ? activeCat : null) || (categories[0]?.id || '');
        setItemForm({
            category_id: defCatId,
            name: '', description: '', price: '', sale_type: 'piece', is_new: false, staff_only: false, set_only: false, banquet_role: '', cost_price: '', sort_order: 0, printer: null, qr_portion_grams: '', portion_pieces: 1
        });
        setEditItem(null);
        setVariants([]);
        setVariantForm({ name: '', price: '' });
        setVariantPrinterEdit(null);
        setPendingItemImage(null);
        setPendingImageUrl(null);
        setImgResults(null);
        setImgQueryDirty(false);
        setItemTab('main');
        setCatSearch(categories.find(c => String(c.id) === String(defCatId))?.name || ''); // seçili kateqoriya adı comboboxda görünsün
        setCatOpen(false);
        if (pendingItemImagePreview) {
            if (pendingItemImagePreview.startsWith('blob:')) URL.revokeObjectURL(pendingItemImagePreview);
            setPendingItemImagePreview(null);
        }
        setItemModal(true);
    };

    const openItemEdit = async (item) => {
        setItemForm({
            category_id: item.category_id,
            name:        item.name,
            description: item.description || '',
            price:       item.price,
            sale_type:   item.sale_type || 'piece',
            is_new:      Boolean(Number(item.is_new || 0)),
            staff_only:  Boolean(Number(item.staff_only || 0)),
            set_only:    Boolean(Number(item.set_only || 0)),
            banquet_role: item.banquet_role || '',
            cost_price:  item.cost_price != null ? Number(item.cost_price) : '',
            sort_order:  item.sort_order,
            printer:     item.printer || null,
            qr_portion_grams: Number(item.qr_portion_grams || 0) || '',
            portion_pieces: Number(item.portion_pieces) || 1,
            is_hourly: Boolean(Number(item.is_hourly || 0))
        });
        setEditItem(item);
        setVariants([]);
        setVariantForm({ name: '', price: '' });
        setVariantPrinterEdit(null);
        setImgResults(null);
        setImgQueryDirty(false);
        setItemTab('main');
        setCatSearch(categories.find(c => String(c.id) === String(item.category_id))?.name || '');
        setCatOpen(false);
        setItemModal(true);
        // Load variants
        try {
            const res = await api.get(`/menu/items/${item.id}/variants`);
            setVariants(res.data.data || []);
        } catch { /* ignore */ }
    };

    // Yeni məhsulda (hələ yaradılmayıb) variantlar LOKAL saxlanır (tmp_ id), submit-də yaradılır
    const isTempVariant = (id) => typeof id === 'string' && id.startsWith('tmp_');

    const saveVariantPrinter = async (variantId, printerName) => {
        setVariants(prev => prev.map(v => v.id === variantId ? { ...v, printer: printerName || null } : v));
        setVariantPrinterEdit(null);
        if (isTempVariant(variantId)) return; // yeni məhsul — lokal
        try { await api.put(`/menu/variants/${variantId}`, { printer: printerName || null }); }
        catch { toast.error('Printer saxlanmadı.'); }
    };

    const addVariant = async () => {
        if (!variantForm.name.trim() || variantForm.price === '') return;
        const focusName = () => setTimeout(() => variantNameRef.current?.focus(), 0);
        // Yeni məhsul → lokal variant (submit-də yaradılacaq)
        if (!editItem) {
            setVariants(prev => [...prev, {
                id: `tmp_${Date.now()}_${prev.length}`,
                name: variantForm.name.trim(),
                price: parseFloat(variantForm.price),
                printer: null,
                sort_order: prev.length,
            }]);
            setVariantForm({ name: '', price: '' });
            focusName();
            return;
        }
        setVariantLoading(true);
        try {
            const res = await api.post(`/menu/items/${editItem.id}/variants`, {
                name: variantForm.name.trim(),
                price: parseFloat(variantForm.price),
                sort_order: variants.length
            });
            setVariants(prev => [...prev, res.data.data]);
            setVariantForm({ name: '', price: '' });
            focusName();
        } catch { toast.error('Variant əlavə edilmədi.'); }
        finally { setVariantLoading(false); }
    };

    const deleteVariant = async (variantId) => {
        if (isTempVariant(variantId)) { setVariants(prev => prev.filter(v => v.id !== variantId)); return; }
        setVariantLoading(true);
        try {
            await api.delete(`/menu/variants/${variantId}`);
            setVariants(prev => prev.filter(v => v.id !== variantId));
        } catch { toast.error('Variant silinmədi.'); }
        finally { setVariantLoading(false); }
    };

    // Variant ad/qiymət inline redaktə — lokal dəyiş, blur-da serverə yaz (yeni məhsulda yalnız lokal)
    const updateVariantLocal = (variantId, patch) =>
        setVariants(prev => prev.map(v => v.id === variantId ? { ...v, ...patch } : v));

    const saveVariantEdit = async (v) => {
        const name = String(v.name || '').trim();
        if (!name || v.price === '' || v.price == null) return;
        if (isTempVariant(v.id)) return; // lokal — updateVariantLocal ilə artıq dəyişilib
        try {
            await api.put(`/menu/variants/${v.id}`, { name, price: parseFloat(v.price) });
        } catch { toast.error('Variant yenilənmədi.'); }
    };

    // Variantlar tabı açılanda ad sahəsinə fokus (klaviatura axını)
    useEffect(() => {
        if (itemModal && itemTab === 'variants') {
            const t = setTimeout(() => variantNameRef.current?.focus(), 60);
            return () => clearTimeout(t);
        }
    }, [itemModal, itemTab]);

    // Sürüşdürərək sırasını dəyiş — mövcud məhsulda serverə yazılır, yenidə lokal
    const reorderVariants = async (from, to) => {
        if (from == null || to == null || from === to) return;
        const next = [...variants];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        setVariants(next);
        if (next.some(v => isTempVariant(v.id))) return; // yeni məhsul — lokal
        try {
            await Promise.all(next.map((v, i) => api.put(`/menu/variants/${v.id}`, { sort_order: i })));
        } catch { toast.error('Sıra yadda saxlanmadı.'); }
    };

    const handleItemSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editItem) {
                await api.put(`/menu/items/${editItem.id}`, {
                    ...itemForm,
                    printer: itemForm.printer || null,
                    active: editItem.active
                });
                // Variant qiymət/adlarını da yadda saxla (blur işləməsə də — "Yadda saxla" ilə persist olsun)
                if (variants.length) {
                    await Promise.all(
                        variants
                            .map(v => {
                                const name = String(v.name || '').trim();
                                const price = parseFloat(v.price);
                                if (!name || !Number.isFinite(price)) return null;
                                return api.put(`/menu/variants/${v.id}`, { name, price });
                            })
                            .filter(Boolean)
                    );
                }
                toast.success('Məhsul yeniləndi.');
            } else {
                // Variantlı məhsulda əsas qiymət boşdur → ən ucuz variantın qiymətini baza qiymət et
                // (backend "mürəkkəb satış üçün qiymət lazımdır" tələb edir; variantlar sifarişdə qiyməti əvəz edir)
                const varPrices = variants.map(v => parseFloat(v.price)).filter(Number.isFinite);
                const needPrice = (itemForm.price === '' || itemForm.price == null);
                const createPayload = (needPrice && varPrices.length)
                    ? { ...itemForm, price: Math.min(...varPrices) }
                    : itemForm;
                const res = await api.post('/menu/items', createPayload);
                const newItemId = res.data.id;
                // Lokal əlavə olunmuş variantları yarat (sıra ilə) + printer override
                for (let i = 0; i < variants.length; i++) {
                    const v = variants[i];
                    const name = String(v.name || '').trim();
                    const price = parseFloat(v.price);
                    if (!name || !Number.isFinite(price)) continue;
                    try {
                        const vr = await api.post(`/menu/items/${newItemId}/variants`, { name, price, sort_order: i });
                        if (v.printer && vr.data?.data?.id) await api.put(`/menu/variants/${vr.data.data.id}`, { printer: v.printer });
                    } catch { /* bir variant alınmasa davam et */ }
                }
                if (pendingItemImage) {
                    const fd = new FormData();
                    fd.append('image', pendingItemImage, pendingItemImage.name);
                    await api.post(`/menu/items/${newItemId}/image`, fd);
                } else if (pendingImageUrl) {
                    // İnternetdən seçilmiş şəkli endirib tətbiq et
                    try { await api.post(`/menu/items/${newItemId}/image-from-url`, { url: pendingImageUrl }); }
                    catch (e) { toast.error('Şəkil əlavə olunmadı, amma məhsul yaradıldı.'); }
                }
                setPendingItemImage(null);
                setPendingImageUrl(null);
                if (pendingItemImagePreview) {
                    if (pendingItemImagePreview.startsWith('blob:')) URL.revokeObjectURL(pendingItemImagePreview);
                    setPendingItemImagePreview(null);
                }
                toast.success(`✓ "${itemForm.name}" menyuya əlavə edildi`);

                // Siyahını refresh et
                const [catRes, allItemsRes] = await Promise.all([
                    api.get('/menu/categories/all'),
                    api.get('/menu/items/all')
                ]);
                setCategories(catRes.data.data || []);
                setItems(allItemsRes.data.data || []);
                setNoveltyIds(allItemsRes.data.novelty_ids || []);
                setNoveltiesCustom(Number(allItemsRes.data.novelties_custom_sorted || 0) === 1);

                // Modal AÇIQ qalır — form fresh sıfırlanır, eyni kateqoriyada davam et
                setItemForm({
                    category_id: itemForm.category_id, // eyni kateqoriyada qal
                    name: '',
                    description: '',
                    price: '',
                    sale_type: itemForm.sale_type, // eyni sale_type-da qal
                    sort_order: 0,
                    printer: itemForm.printer, // eyni printer-də qal
                    qr_portion_grams: itemForm.qr_portion_grams, // eyni porsiyada qal
                    portion_pieces: itemForm.portion_pieces, // eyni ədəd/porsiyada qal
                    is_hourly: itemForm.is_hourly // saatlıq seçimi qalsın
                });

                // Köhnə şəkil axtarışını təmizlə (yeni ad üçün yenidən qurulsun)
                setImgQueryDirty(false);
                setImgQueryEdit('');
                setImgResults(null);
                setImgQuery('');
                // Variantları sıfırla (növbəti məhsul üçün)
                setVariants([]);
                setVariantForm({ name: '', price: '' });

                // "Əsas" tabına qayıt (Ad sahəsi orada) + həmin sahəyə fokus
                setItemTab('main');
                setCatSearch(categories.find(c => String(c.id) === String(itemForm.category_id))?.name || '');
                setTimeout(() => {
                    const nameInput = document.querySelector('input[name="item-name"]');
                    if (nameInput) nameInput.focus();
                }, 60);

                return;
            }
            setItemModal(false);
            loadAll();
        } catch (err) {
            if (err.response?.status === 403) setShowUpgradeModal(true);
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const handleItemToggle = async (item) => {
        try {
            await api.put(`/menu/items/${item.id}`, {
                category_id: item.category_id,
                name:        item.name,
                description: item.description,
                price:       item.price,
                sale_type:   item.sale_type || 'piece',
                sort_order:  item.sort_order,
                active:      item.active ? 0 : 1
            });
            toast.success(item.active ? 'Deaktiv edildi.' : 'Aktiv edildi.');
            loadAll();
        } catch {
            toast.error('Xəta baş verdi.');
        }
    };

    const handleItemDelete = async (item) => {
        if (!(await confirm(`"${item.name}" silinsin?`))) return;
        try {
            await api.delete(`/menu/items/${item.id}`);
            toast.success('Məhsul silindi.');
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinə bilmədi.');
        }
    };

    // ── Maya ≥ / satış qiymətinə yaxın olanlar (səhv qoyulmuş qiymətləri tapmaq) ──
    const isLowMargin = (item) => {
        const maya = Number(item.recipe_cost) > 0 ? Number(item.recipe_cost) : Number(item.cost_price || 0);
        if (maya <= 0) return false;
        let price = Number(item.price || 0);
        if (item.variants && item.variants.length) {
            const vp = item.variants.map(v => Number(v.price || 0)).filter(p => p > 0);
            if (vp.length) price = Math.min(...vp);
        }
        if (price <= 0) return false;
        // Satış qiyməti maya + X%-dən az olanlar (mənfəət azdır → səhv qiymət ola bilər)
        const pct = Number(marginPct) || 0;
        return price < maya * (1 + pct / 100);
    };
    // ── Filtrlər (Mənfəət / Şəkilsizlər / Yalnız admin-kassir) — hamısı eyni qayda ilə ──
    // Filtr açıqdırsa: kateqoriyalar (və «Yeniliklər») yalnız uyğun məhsulu varsa görünür, say = uyğun say.
    const staffOnlyCatIds = new Set(categories.filter(c => Number(c.staff_only || 0) === 1).map(c => c.id));
    const isStaffOnlyItem = (item) => Number(item.staff_only || 0) === 1 || staffOnlyCatIds.has(item.category_id);
    const activeFilterCount = (lowMarginFilter ? 1 : 0) + (noImageFilter ? 1 : 0) + (staffOnlyFilter ? 1 : 0) + (setOnlyFilter ? 1 : 0);
    const anyFilter = activeFilterCount > 0;
    const passesFilters = (item) => {
        if (lowMarginFilter && !isLowMargin(item)) return false;
        if (noImageFilter && item.image_url) return false;
        if (staffOnlyFilter && !isStaffOnlyItem(item)) return false;
        if (setOnlyFilter && Number(item.set_only || 0) !== 1) return false;
        return true;
    };
    const filterCountByCat = anyFilter
        ? items.reduce((m, i) => { if (passesFilters(i)) m[i.category_id] = (m[i.category_id] || 0) + 1; return m; }, {})
        : null;
    const visibleCategories = anyFilter ? categories.filter(c => (filterCountByCat[c.id] || 0) > 0) : categories;
    const clearFilters = () => { setLowMarginFilter(false); setNoImageFilter(false); setStaffOnlyFilter(false); setSetOnlyFilter(false); };

    // Filtr seçili kateqoriyanı gizlədirsə, «Hamısı»na qayıt (boş siyahı qalmasın)
    useEffect(() => {
        if (!anyFilter || !activeCat) return;
        if (activeCat === NOVELTIES ? noveltyVisibleCountRef.current === 0 : !(filterCountByCat?.[activeCat] > 0)) setActiveCat(null);
    }, [lowMarginFilter, noImageFilter, staffOnlyFilter, marginPct]); // eslint-disable-line react-hooks/exhaustive-deps

    // «Yeniliklər»: server sırası (əl sırası + superadmin rejimi), sonra siyahıda olmayan yeni məhsullar
    const noveltyItems = (() => {
        const newOnes = items.filter((i) => Number(i.is_new || 0) === 1);
        const byId = new Map(newOnes.map((i) => [i.id, i]));
        const ordered = noveltyIds.map((id) => byId.get(id)).filter(Boolean);
        const seen = new Set(ordered.map((i) => i.id));
        return [...ordered, ...newOnes.filter((i) => !seen.has(i.id))];
    })();
    const noveltyVisibleCount = anyFilter ? noveltyItems.filter(passesFilters).length : noveltyItems.length;
    noveltyVisibleCountRef.current = noveltyVisibleCount;
    const filteredItems = (activeCat === NOVELTIES
        ? noveltyItems
        : activeCat
            ? items.filter(i => i.category_id === activeCat)
            : items
    ).filter((item) => {
        if (!passesFilters(item)) return false;
        const query = itemSearch.trim().toLowerCase();
        if (!query) return true;
        return String(item.name || '').toLowerCase().includes(query)
            || String(item.description || '').toLowerCase().includes(query)
            || String(item.cat_name || '').toLowerCase().includes(query);
    });

    // ── Məhsul sırası (kateqoriya daxilində) ─────────────────────────────────
    // Yalnız BİR kateqoriya seçiləndə və axtarış/filtr olmayanda aktivdir (tam siyahı görünür).
    const canReorderItems = Boolean(activeCat) && !itemSearch.trim() && !anyFilter;
    const [itemDragId, setItemDragId] = useState(null);
    const [itemDragOverId, setItemDragOverId] = useState(null);
    const saveItemOrder = async (orderedIds) => {
        const catId = activeCat;
        if (catId === NOVELTIES) {
            setNoveltyIds(orderedIds);
            setNoveltiesCustom(true);
            try {
                await api.post('/menu/novelties/reorder', { ids: orderedIds });
                toast.success('Yeniliklərin sırası yeniləndi. QR menyuda da bu sıra ilə görünəcək.', { id: 'item-reorder' });
            } catch (err) {
                toast.error(err.response?.data?.msg || 'Sıra yenilənə bilmədi.');
                loadAll();
            }
            return;
        }
        // Optimistik: həmin kateqoriyanın məhsullarını yeni sıra ilə öz yerlərinə qoy
        setItems((prev) => {
            const pos = [];
            prev.forEach((it, i) => { if (it.category_id === catId) pos.push(i); });
            const byId = new Map(prev.filter((it) => it.category_id === catId).map((it) => [it.id, it]));
            const next = [...prev];
            orderedIds.forEach((id, k) => { if (pos[k] !== undefined && byId.has(id)) next[pos[k]] = { ...byId.get(id), sort_order: k + 1 }; });
            return next;
        });
        setCategories((prev) => prev.map((c) => (c.id === catId ? { ...c, items_custom_sorted: 1 } : c)));
        try {
            await api.post('/menu/items/reorder', { category_id: catId, ids: orderedIds });
            toast.success('Sıra yeniləndi. QR menyu və ofisiant ekranında da bu sıra ilə görünəcək.', { id: 'item-reorder' });
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Sıra yenilənə bilmədi.');
            loadAll();
        }
    };
    const activeCatObj = categories.find((c) => c.id === activeCat);
    const activeCatCustomSorted = activeCat === NOVELTIES
        ? noveltiesCustom
        : Number(activeCatObj?.items_custom_sorted || 0) === 1;
    // Sıfırla → həm burada, həm QR-da superadmin «Məhsul sıralaması» ilə düzülür (server yenidən sıralayır)
    const resetItemOrder = async () => {
        if (!activeCat) return;
        if (!(await confirm('Əl ilə qoyduğunuz sıra silinsin? Məhsullar superadmin sıralamasına görə düzüləcək.'))) return;
        try {
            const r = activeCat === NOVELTIES
                ? await api.post('/menu/novelties/auto-order')
                : await api.post(`/menu/categories/${activeCat}/auto-item-order`);
            toast.success(r.data?.msg || 'Avtomatik sıralama bərpa olundu.');
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Alınmadı.');
        }
    };
    const moveItem = (itemId, dir) => {
        if (!canReorderItems) return;
        const ids = filteredItems.map((i) => i.id);
        const idx = ids.indexOf(itemId);
        const swap = idx + dir;
        if (idx < 0 || swap < 0 || swap >= ids.length) return;
        [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
        saveItemOrder(ids);
    };
    const handleItemDrop = (targetId) => {
        setItemDragOverId(null);
        const fromId = itemDragId;
        setItemDragId(null);
        if (!canReorderItems || !fromId || fromId === targetId) return;
        const ids = filteredItems.map((i) => i.id);
        const from = ids.indexOf(fromId);
        const to = ids.indexOf(targetId);
        if (from < 0 || to < 0) return;
        ids.splice(from, 1);
        ids.splice(to, 0, fromId);
        saveItemOrder(ids);
    };
    const ReorderArrows = ({ item, index, total }) => (
        <div className="flex flex-col gap-0.5 shrink-0">
            <button type="button" onClick={() => moveItem(item.id, -1)} disabled={index === 0} title="Yuxarı"
                className="w-6 h-5 flex items-center justify-center rounded bg-gray-100 text-[10px] text-gray-600 hover:bg-gray-200 disabled:opacity-30">▲</button>
            <button type="button" onClick={() => moveItem(item.id, 1)} disabled={index === total - 1} title="Aşağı"
                className="w-6 h-5 flex items-center justify-center rounded bg-gray-100 text-[10px] text-gray-600 hover:bg-gray-200 disabled:opacity-30">▼</button>
        </div>
    );

    const getPrinterInfo = (printerName) => {
        const found = printers.find(p => p.name === printerName);
        if (found) return { ...found, icon: getPrinterIcon(found.name) };
        return { name: printerName || 'xp-metbex', label: printerName || 'Mətbəx', icon: '🖨️' };
    };

    const getSaleTypeLabel = (saleType) => {
        if (saleType === 'weight') return 'Çəki ilə (kq)';
        if (saleType === 'liter') return 'Litr ilə (lt)';
        return 'Ədəd ilə';
    };

    const inputClass = "w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary-500 transition-all";
    const canUseMenuSets = hasPlanFeature(user?.tenant_plan, 'menu_sets');

    if (loading) {
        const loadingContent = (
            <div className="flex items-center justify-center h-48 text-gray-400">Yüklənir...</div>
        );

        return embedded ? loadingContent : <Layout>{loadingContent}</Layout>;
    }

    // Kateqoriya idarəetmə paneli — desktop sidebar + mobil bottom-sheet-də təkrar işlənir
    const categoriesPanel = (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-gray-700 text-sm">📂 Kateqoriyalar</h3>
                <button
                    onClick={openCatCreate}
                    className="text-xs font-bold text-primary-500 hover:text-primary-700"
                >
                    + Əlavə et
                </button>
            </div>
            <div className="p-2">
                {noveltyVisibleCount > 0 && (
                    <div
                        onClick={() => { setActiveCat(NOVELTIES); setCatSheetOpen(false); }}
                        className={`flex items-center gap-2 px-2.5 py-2.5 rounded-lg cursor-pointer transition-all mb-1 ${
                            activeCat === NOVELTIES ? 'bg-amber-50 text-amber-800 ring-1 ring-amber-200' : 'hover:bg-amber-50/60 text-gray-700'
                        }`}
                        title="«Yenilik» tiki olan məhsullar. Sırasını burada ▲▼ ilə dəyişin."
                    >
                        <span className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center text-base flex-shrink-0">✨</span>
                        <span className="flex-1 min-w-0 truncate text-sm font-semibold leading-tight">Yeniliklər</span>
                        {noveltiesCustom && <span className="text-[10px] font-semibold text-emerald-600" title="Əl ilə sıralanıb">əl sırası</span>}
                        <span className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 bg-amber-100 text-amber-700">{noveltyVisibleCount}</span>
                    </div>
                )}
                {categories.length === 0 ? (
                    <div className="p-4 text-center text-gray-400 text-sm">
                        Kateqoriya yoxdur.
                    </div>
                ) : (
                    visibleCategories.map((cat, ci, arr) => {
                        const printer = getPrinterInfo(cat.printer);
                        return (
                            <div
                                key={cat.id}
                                draggable
                                onDragStart={() => handleDragStart(cat.id)}
                                onDragOver={(e) => handleDragOver(e, cat.id)}
                                onDragLeave={() => setDragOverId(null)}
                                onDrop={() => handleDrop(cat.id)}
                                onClick={() => { setActiveCat(cat.id); setCatSheetOpen(false); }}
                                className={`group relative flex items-center gap-2 px-2.5 py-2.5 rounded-lg cursor-move transition-all mb-0.5 border-t-2 ${
                                    dragId && dragOverId === cat.id && dragId !== cat.id ? 'border-t-red-500' : 'border-t-transparent'
                                } ${
                                    activeCat === cat.id
                                        ? 'bg-primary-50 text-primary-700'
                                        : 'hover:bg-gray-50 text-gray-600'
                                } ${!cat.active ? 'opacity-50' : ''} ${dragId === cat.id ? 'opacity-40' : ''}`}
                            >
                                {cat.image_url ? (
                                    <img src={`${getAssetBaseUrl()}${cat.image_url}`} alt={cat.name}
                                        className="w-7 h-7 rounded-lg object-cover flex-shrink-0" />
                                ) : (
                                    <span className="text-base flex-shrink-0">{printer.icon}</span>
                                )}
                                <span className="flex-1 min-w-0 truncate text-sm font-medium leading-tight" title={cat.name}>
                                    {cat.name}
                                </span>
                                <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${anyFilter ? 'bg-primary-100 text-primary-700 font-bold' : 'bg-gray-100 text-gray-500'} ${activeCat === cat.id ? 'sm:opacity-0' : 'sm:group-hover:opacity-0'} transition-opacity`}
                                    title={anyFilter ? 'Filtrə uyğun məhsul sayı' : undefined}>
                                    {anyFilter ? (filterCountByCat?.[cat.id] || 0) : cat.item_count}
                                </span>
                                {/* Üzən idarə düymələri */}
                                <div onClick={(e) => e.stopPropagation()}
                                    className={`absolute right-1.5 top-1/2 -translate-y-1/2 items-center gap-0.5 rounded-lg bg-white/95 px-1 py-0.5 shadow-sm ring-1 ring-gray-100 ${activeCat === cat.id ? 'flex' : 'hidden sm:group-hover:flex'}`}>
                                    <button type="button" onClick={() => moveCategory(cat.id, -1)} disabled={ci === 0}
                                        className="text-[11px] leading-none text-gray-400 hover:text-primary-600 disabled:opacity-20 px-0.5" title="Yuxarı">▲</button>
                                    <button type="button" onClick={() => moveCategory(cat.id, 1)} disabled={ci === arr.length - 1}
                                        className="text-[11px] leading-none text-gray-400 hover:text-primary-600 disabled:opacity-20 px-0.5" title="Aşağı">▼</button>
                                    <button onClick={(e) => { e.stopPropagation(); openCatEdit(cat); }}
                                        className="text-xs text-gray-400 hover:text-primary-500 px-0.5" title="Redaktə">✏️</button>
                                    <button onClick={(e) => { e.stopPropagation(); handleCatToggle(cat); }}
                                        className="text-xs text-gray-400 hover:text-amber-500 px-0.5" title="Aktiv/Passiv">{cat.active ? '👁️' : '🚫'}</button>
                                    {cat.item_count === 0 && (
                                        <button onClick={(e) => { e.stopPropagation(); handleCatDelete(cat); }}
                                            className="text-xs text-gray-400 hover:text-red-500 px-0.5" title="Sil">🗑️</button>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );

    const content = (
        <>
            {cropState && (
                <ImageCropModal
                    file={cropState.file}
                    aspect={1}
                    title="Məhsul şəklini kəs (kvadrat)"
                    onCancel={() => setCropState(null)}
                    onConfirm={async (cropped) => {
                        const target = cropState.target;
                        setCropState(null);
                        if (target === 'edit') await uploadEditItemImage(cropped);
                        else await setPendingItemImageFromFile(cropped);
                    }}
                />
            )}
            <UpgradeModal
                open={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                title="Şəkil yükləmə bu paketdə bağlıdır"
                message="Məhsul və kateqoriya şəkilləri yalnız Pro paketdə aktivdir."
                plans={['basic', 'pro']}
                currentPlan={user?.tenant_plan}
            />
            <MenuPrintModal
                open={printModalOpen}
                onClose={() => setPrintModalOpen(false)}
                printers={printers}
            />
            <div className="flex items-center justify-between mb-4">
                {!embedded && (
                    <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'serif' }}>
                        Menyu idarəsi
                    </h1>
                )}
            </div>

            {/* Tab seçimi */}
            {canUseMenuSets && (
                <div className="flex gap-1 mb-5 p-1 bg-gray-100 rounded-xl w-fit">
                    <button
                        onClick={() => setActiveTab('menu')}
                        className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${
                            activeTab === 'menu'
                                ? 'bg-white text-primary-600 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        🍽️ Menyu
                    </button>
                    <button
                        onClick={() => setActiveTab('sets')}
                        className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${
                            activeTab === 'sets'
                                ? 'bg-white text-primary-600 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        🎁 Setlər
                    </button>
                </div>
            )}

            {/* SETLƏR Tab */}
            {canUseMenuSets && activeTab === 'sets' && (
                <MenuSetManager compact={embedded} />
            )}

            {/* MENYU Tab */}
            {activeTab === 'menu' && (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">

                {/* SOL — KATEQORİYALAR (mobildə gizli; yuxarıdakı "Kateqoriyalar" düyməsi ilə açılır) */}
                <div className="hidden lg:block lg:col-span-1">
                    {categoriesPanel}
                </div>

                {/* SAĞ — MƏHSULLAR */}
                <div className="lg:col-span-3 min-w-0">
                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                        <div className="flex flex-col gap-3 border-b border-gray-100 px-5 py-3">
                            <div>
                                <h3 className="text-sm font-semibold text-gray-700">
                                    🍽️ Məhsullar ({filteredItems.length})
                                </h3>
                                <p className="mt-1 text-xs text-gray-400">
                                    {canReorderItems
                                        ? 'Sıranı ▲▼ oxları ilə və ya sətri tutub sürüşdürərək dəyişin.'
                                        : 'Məhsul adı, təsvir və ya kateqoriya ilə axtarın. Sıralamaq üçün kateqoriya seçin.'}
                                </p>
                                {activeCat && activeCatCustomSorted && (
                                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700 ring-1 ring-emerald-200">
                                            QR menyuda bu sıra ilə görünür
                                        </span>
                                        <button type="button" onClick={resetItemOrder}
                                            className="font-semibold text-gray-500 underline decoration-dotted hover:text-gray-700">
                                            ↺ Sıfırla (superadmin sıralaması)
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="flex w-full flex-col gap-2 lg:flex-row lg:flex-nowrap lg:items-center">
                                <div className="flex gap-2 lg:contents">
                                    {/* Mobil: kateqoriya seçimi (desktopda sol panel var) */}
                                    <button
                                        type="button"
                                        onClick={() => setCatSheetOpen(true)}
                                        className="flex min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-bold text-gray-600 transition-all hover:bg-gray-50 lg:hidden"
                                    >
                                        <span className="truncate">📂 {activeCat === NOVELTIES ? '✨ Yeniliklər' : activeCat ? (categories.find(c => c.id === activeCat)?.name || 'Kateqoriya') : 'Kateqoriyalar'}</span> ▾
                                    </button>

                                    {/* Axtarış — desktopda solda */}
                                    <input
                                        type="text"
                                        value={itemSearch}
                                        onChange={(e) => setItemSearch(e.target.value)}
                                        placeholder="🔍 Məhsul axtar..."
                                        className="hidden min-w-[160px] rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-700 focus:border-primary-400 focus:outline-none lg:mr-auto lg:block lg:w-64"
                                    />

                                    {/* Filtrlər — bütün filtrlər bir paneldə (mobil + desktop) */}
                                    <div className="relative shrink-0">
                                        <button
                                            type="button"
                                            onClick={() => setFiltersOpen(o => !o)}
                                            className={`flex h-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border px-3 py-2 text-xs font-bold transition-all ${anyFilter ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
                                        >
                                            <span>⚙️ Filtrlər</span>
                                            {anyFilter && (
                                                <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] text-white">{activeFilterCount}</span>
                                            )}
                                            <span className="text-[10px]">{filtersOpen ? '▴' : '▾'}</span>
                                        </button>

                                        {filtersOpen && (
                                            <>
                                                <div className="fixed inset-0 z-30" onClick={() => setFiltersOpen(false)} />
                                                <div className="absolute right-0 top-full z-40 mt-2 w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
                                                    <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
                                                        <span className="text-sm font-bold text-gray-800">Filtrlər</span>
                                                        {anyFilter && (
                                                            <button type="button" onClick={clearFilters} className="text-xs font-semibold text-gray-400 hover:text-gray-600">Təmizlə</button>
                                                        )}
                                                    </div>
                                                    <div className="divide-y divide-gray-100">
                                                        {/* Mənfəət */}
                                                        <div className={`px-4 py-3 transition-colors ${lowMarginFilter ? 'bg-rose-50/60' : ''}`}>
                                                            <label className="flex cursor-pointer items-start gap-3">
                                                                <input type="checkbox" checked={lowMarginFilter}
                                                                    onChange={(e) => setLowMarginFilter(e.target.checked)}
                                                                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-rose-600" />
                                                                <span className="min-w-0 flex-1">
                                                                    <span className="block text-sm font-semibold text-gray-800">⚠️ Az mənfəət</span>
                                                                    <span className="block text-[11px] leading-snug text-gray-500">Satış qiyməti mayadan bu faizdən az yuxarı olanlar (səhv qiyməti tapmaq üçün)</span>
                                                                </span>
                                                            </label>
                                                            <div className="mt-2 flex items-center gap-2 pl-7 text-xs text-gray-500">
                                                                Mənfəət &lt;
                                                                <input type="number" min="0" step="1" value={marginPct}
                                                                    onChange={(e) => setMarginPct(e.target.value)}
                                                                    className="w-14 rounded-lg border border-rose-200 px-2 py-1 text-center text-xs font-bold text-rose-600 focus:border-rose-400 focus:outline-none" />
                                                                %
                                                            </div>
                                                        </div>
                                                        {/* Şəkilsizlər */}
                                                        {canUseMenuImages && (
                                                            <label className={`flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors ${noImageFilter ? 'bg-amber-50/60' : ''}`}>
                                                                <input type="checkbox" checked={noImageFilter}
                                                                    onChange={(e) => setNoImageFilter(e.target.checked)}
                                                                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-amber-600" />
                                                                <span className="min-w-0 flex-1">
                                                                    <span className="block text-sm font-semibold text-gray-800">📷 Şəkilsizlər</span>
                                                                    <span className="block text-[11px] leading-snug text-gray-500">Yalnız şəkli olmayan məhsullar</span>
                                                                </span>
                                                            </label>
                                                        )}
                                                        {/* Yalnız admin/kassir */}
                                                        <label className={`flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors ${staffOnlyFilter ? 'bg-sky-50/60' : ''}`}>
                                                            <input type="checkbox" checked={staffOnlyFilter}
                                                                onChange={(e) => setStaffOnlyFilter(e.target.checked)}
                                                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-sky-600" />
                                                            <span className="min-w-0 flex-1">
                                                                <span className="block text-sm font-semibold text-gray-800">🔒 Yalnız admin/kassir</span>
                                                                <span className="block text-[11px] leading-snug text-gray-500">QR menyuda görünməyən, ofisiantın vura bilmədiyi məhsullar (məhsulun öz tiki və ya kateqoriyası ilə)</span>
                                                            </span>
                                                        </label>
                                                        {/* Yalnız set/banket içində */}
                                                        <label className={`flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors ${setOnlyFilter ? 'bg-violet-50/60' : ''}`}>
                                                            <input type="checkbox" checked={setOnlyFilter}
                                                                onChange={(e) => setSetOnlyFilter(e.target.checked)}
                                                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-violet-600" />
                                                            <span className="min-w-0 flex-1">
                                                                <span className="block text-sm font-semibold text-gray-800">🧩 Yalnız set/banket içində</span>
                                                                <span className="block text-[11px] leading-snug text-gray-500">Menyuda tək satılmayan, yalnız set və banketdə işlədilən məhsullar</span>
                                                            </span>
                                                        </label>
                                                    </div>
                                                    <div className="border-t border-gray-100 px-4 py-2.5">
                                                        <button type="button" onClick={() => setFiltersOpen(false)}
                                                            className="w-full rounded-lg bg-gray-900 py-2 text-xs font-bold text-white hover:bg-gray-800">
                                                            Göstər ({filteredItems.length})
                                                        </button>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => setPrintModalOpen(true)}
                                        className="shrink-0 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-bold text-gray-600 transition-all hover:bg-gray-50"
                                        title="Menyunu çap et / PDF ixrac et"
                                    >
                                        🖨️<span className="hidden sm:inline"> Çap / PDF</span>
                                    </button>
                                </div>

                                {/* Axtarış — mobildə tam en */}
                                <input
                                    type="text"
                                    value={itemSearch}
                                    onChange={(e) => setItemSearch(e.target.value)}
                                    placeholder="🔍 Məhsul axtar..."
                                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-700 focus:border-primary-400 focus:outline-none lg:hidden"
                                />

                                {/* Əlavə et — mobildə tam en, desktopda auto */}
                                <button
                                    onClick={openItemCreate}
                                    className="w-full whitespace-nowrap rounded-lg px-3 py-2.5 text-xs font-bold text-white transition-all lg:w-auto lg:py-2"
                                    style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                                >
                                    + Məhsul əlavə et
                                </button>
                            </div>

                            {/* Aktiv filtr çipləri */}
                            {anyFilter && (
                                <div className="flex flex-wrap items-center gap-1.5">
                                    {lowMarginFilter && (
                                        <span className="flex items-center gap-1 rounded-full bg-rose-50 py-0.5 pl-2.5 pr-1 text-xs font-semibold text-rose-700 ring-1 ring-rose-200">
                                            ⚠️ Mənfəət &lt; {marginPct}%
                                            <button type="button" onClick={() => setLowMarginFilter(false)} className="flex h-5 w-5 items-center justify-center rounded-full hover:bg-rose-100">✕</button>
                                        </span>
                                    )}
                                    {noImageFilter && (
                                        <span className="flex items-center gap-1 rounded-full bg-amber-50 py-0.5 pl-2.5 pr-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
                                            📷 Şəkilsizlər
                                            <button type="button" onClick={() => setNoImageFilter(false)} className="flex h-5 w-5 items-center justify-center rounded-full hover:bg-amber-100">✕</button>
                                        </span>
                                    )}
                                    {staffOnlyFilter && (
                                        <span className="flex items-center gap-1 rounded-full bg-sky-50 py-0.5 pl-2.5 pr-1 text-xs font-semibold text-sky-700 ring-1 ring-sky-200">
                                            🔒 Yalnız admin/kassir
                                            <button type="button" onClick={() => setStaffOnlyFilter(false)} className="flex h-5 w-5 items-center justify-center rounded-full hover:bg-sky-100">✕</button>
                                        </span>
                                    )}
                                    {setOnlyFilter && (
                                        <span className="flex items-center gap-1 rounded-full bg-violet-50 py-0.5 pl-2.5 pr-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-200">
                                            🧩 Yalnız set/banket
                                            <button type="button" onClick={() => setSetOnlyFilter(false)} className="flex h-5 w-5 items-center justify-center rounded-full hover:bg-violet-100">✕</button>
                                        </span>
                                    )}
                                    <button type="button" onClick={clearFilters} className="px-1 text-xs font-semibold text-gray-400 hover:text-gray-600">Hamısını təmizlə</button>
                                </div>
                            )}
                        </div>

                        {/* Filter tabs — mobildə gizli (yuxarıdakı "Kateqoriyalar" düyməsi filtr edir) */}
                        <div className="hidden gap-2 px-4 py-3 border-b border-gray-100 overflow-x-auto lg:flex">
                            <button
                                onClick={() => setActiveCat(null)}
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                                    !activeCat
                                        ? 'bg-primary-500 text-white'
                                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                }`}
                            >
                                Hamısı ({items.length})
                            </button>
                            {noveltyVisibleCount > 0 && (
                                <button
                                    onClick={() => setActiveCat(NOVELTIES)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                                        activeCat === NOVELTIES ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                                    }`}
                                >
                                    ✨ Yeniliklər ({noveltyVisibleCount})
                                </button>
                            )}
                            {visibleCategories.map(cat => {
                                const printer = getPrinterInfo(cat.printer);
                                return (
                                    <button
                                        key={cat.id}
                                        onClick={() => setActiveCat(cat.id)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1 ${
                                            activeCat === cat.id
                                                ? 'bg-primary-500 text-white'
                                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                        }`}
                                    >
                                        <span>{printer.icon}</span>
                                        {cat.name} ({cat.item_count})
                                    </button>
                                );
                            })}
                        </div>

                        {/* Yalnız məhsul siyahısı üçün ayrıca scroll (bütün səhifə yox) */}
                        <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                        {filteredItems.length === 0 ? (
                            <div className="p-12 text-center text-gray-400">
                                Bu filtrə uyğun məhsul yoxdur.
                            </div>
                        ) : (
                            <>
                                {/* ── Mobile card list (hidden on sm+) ── */}
                                <div className="sm:hidden divide-y divide-gray-100">
                                    {filteredItems.map((item, itemIdx) => {
                                        const cat = categories.find(c => c.id === item.category_id);
                                        const effectivePrinter = getPrinterInfo(item.printer || cat?.printer);
                                        const hasItemPrinter = Boolean(item.printer);
                                        return (
                                            <div key={item.id} id={`menu-item-m-${item.id}`}
                                                className={`flex items-center gap-3 px-4 py-3 transition-all ${highlightItemId === item.id ? 'bg-primary-50 ring-2 ring-primary-400 rounded-lg' : ''}`}>
                                                {canReorderItems && <ReorderArrows item={item} index={itemIdx} total={filteredItems.length} />}
                                                {item.image_url ? (
                                                    <img src={`${getAssetBaseUrl()}${item.image_url}`} alt={item.name}
                                                        className="w-11 h-11 rounded-xl object-cover border border-gray-200 shrink-0" />
                                                ) : (
                                                    <div className="w-11 h-11 rounded-xl border border-dashed border-gray-200 bg-gray-50 flex items-center justify-center text-gray-300 shrink-0">🍽️</div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <span className="font-semibold text-sm text-gray-800 truncate">{item.name}</span>
                                                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${item.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-500'}`}>
                                                            {item.active ? 'Aktiv' : 'Passiv'}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                                        <span className="text-xs text-gray-400">{item.cat_name}</span>
                                                        {item.variants && item.variants.length > 0 ? (
                                                            <span className="text-xs font-bold text-primary-600 whitespace-nowrap">
                                                                {item.variants.map(v => {
                                                                    const vm = Number(v.recipe_cost || 0);
                                                                    return `${v.name}: ${parseFloat(v.price).toFixed(2)}₼${vm > 0 ? ` (maya ${vm.toFixed(2)})` : ''}`;
                                                                }).join(' · ')}
                                                            </span>
                                                        ) : (
                                                            <span className="text-xs font-bold text-primary-600 whitespace-nowrap">{parseFloat(item.price).toFixed(2)} ₼</span>
                                                        )}
                                                        <span className={`text-[10px] font-mono flex items-center gap-0.5 ${hasItemPrinter ? 'text-blue-600' : 'text-gray-400'}`}>
                                                            {effectivePrinter.icon} {effectivePrinter.name}
                                                        </span>
                                                    </div>
                                                </div>
                                                {/* 2x2 düymələr: kart hündürləşməsin */}
                                                <div className="grid grid-cols-2 gap-1 shrink-0">
                                                    <button onClick={() => openItemEdit(item)} title="Redaktə et"
                                                        className="px-2 py-1 rounded-lg text-xs font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all">
                                                        ✏️
                                                    </button>
                                                    {canUseRecipes && (
                                                        <button onClick={() => openTexkarta(item)} title="Texkarta"
                                                            className="px-2 py-1 rounded-lg text-xs font-semibold bg-primary-50 text-primary-600 hover:bg-primary-100 transition-all">
                                                            📋
                                                        </button>
                                                    )}
                                                    <button onClick={() => handleItemToggle(item)} title={item.active ? 'Deaktiv et' : 'Aktiv et'}
                                                        className="px-2 py-1 rounded-lg text-xs font-semibold bg-amber-50 text-amber-600 hover:bg-amber-100 transition-all">
                                                        {item.active ? '🙈' : '👁️'}
                                                    </button>
                                                    <button onClick={() => handleItemDelete(item)} title="Sil"
                                                        className="px-2 py-1 rounded-lg text-xs font-semibold bg-red-50 text-red-500 hover:bg-red-100 transition-all">
                                                        🗑️
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* ── Desktop table (hidden on mobile) ── */}
                                <table className="hidden sm:table w-full table-fixed">
                                    <thead className="sticky top-0 z-10">
                                        <tr className="bg-gray-50">
                                            <th className={`${canReorderItems ? 'w-20' : 'w-12'} px-1.5 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase bg-gray-50`}>Şəkil</th>
                                            <th className="w-40 px-1.5 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase bg-gray-50">Ad</th>
                                            <th className="w-20 px-1.5 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase bg-gray-50">Kateqoriya</th>
                                            <th className="w-28 px-1.5 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase bg-gray-50">Printer</th>
                                            <th className="w-[68px] px-1.5 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase bg-gray-50">Satış növü</th>
                                            <th className="w-16 px-1.5 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase bg-gray-50">Maya dəyəri</th>
                                            <th className="w-[76px] px-1.5 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase bg-gray-50">Qiymət</th>
                                            <th className="w-16 px-1.5 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase bg-gray-50">Status</th>
                                            <th className="sticky right-0 z-20 w-24 px-1.5 py-2.5 text-right text-xs font-semibold text-gray-400 uppercase bg-gray-50 shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.15)]">Əməliyyat</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredItems.map((item, itemIdx) => {
                                            const cat            = categories.find(c => c.id === item.category_id);
                                            const effectivePrinter = getPrinterInfo(item.printer || cat?.printer);
                                            const hasItemPrinter   = Boolean(item.printer);
                                            return (
                                                <tr key={item.id} id={`menu-item-${item.id}`}
                                                    draggable={canReorderItems}
                                                    onDragStart={canReorderItems ? () => setItemDragId(item.id) : undefined}
                                                    onDragOver={canReorderItems ? (e) => { e.preventDefault(); if (itemDragOverId !== item.id) setItemDragOverId(item.id); } : undefined}
                                                    onDragLeave={canReorderItems ? () => setItemDragOverId(null) : undefined}
                                                    onDrop={canReorderItems ? (e) => { e.preventDefault(); handleItemDrop(item.id); } : undefined}
                                                    onDragEnd={canReorderItems ? () => { setItemDragId(null); setItemDragOverId(null); } : undefined}
                                                    className={`border-t border-gray-50 transition-colors ${canReorderItems ? 'cursor-grab' : ''} ${itemDragId === item.id ? 'opacity-40' : ''} ${itemDragOverId === item.id && itemDragId !== item.id ? 'border-t-2 border-t-primary-500' : ''} ${highlightItemId === item.id ? 'bg-primary-50 ring-2 ring-inset ring-primary-400' : 'hover:bg-gray-50'}`}>
                                                    <td className="px-1.5 py-2.5 align-top">
                                                        <div className="flex items-center gap-1">
                                                        {canReorderItems && <ReorderArrows item={item} index={itemIdx} total={filteredItems.length} />}
                                                        {item.image_url ? (
                                                            <img src={`${getAssetBaseUrl()}${item.image_url}`} alt={item.name} draggable={false}
                                                                className="w-10 h-10 rounded-lg object-cover border border-gray-200" />
                                                        ) : (
                                                            <div className="w-10 h-10 rounded-lg border border-dashed border-gray-200 bg-gray-50 flex items-center justify-center text-gray-300 text-lg">🍽️</div>
                                                        )}
                                                        </div>
                                                    </td>
                                                    <td className="px-1.5 py-2.5 align-top">
                                                        <div className="font-semibold text-sm text-gray-800 leading-tight break-words line-clamp-2">{item.name}</div>
                                                        {item.description && <div className="text-xs text-gray-400 mt-0.5 line-clamp-1">{item.description}</div>}
                                                    </td>
                                                    <td className="px-1.5 py-2.5 align-top text-sm text-gray-500"><div className="line-clamp-2 break-words leading-tight">{item.cat_name}</div></td>
                                                    <td className="px-1.5 py-2.5 align-top overflow-hidden">
                                                        <span className={`text-xs font-mono flex items-center gap-1 min-w-0 ${hasItemPrinter ? 'text-blue-600 font-bold' : 'text-gray-400'}`}>
                                                            <span className="shrink-0">{effectivePrinter.icon}</span>
                                                            <span className="truncate">{effectivePrinter.name}</span>
                                                            {hasItemPrinter && <span className="text-[9px] bg-blue-100 text-blue-600 rounded px-1 shrink-0">xüsusi</span>}
                                                        </span>
                                                    </td>
                                                    <td className="px-1.5 py-2.5 align-top text-sm text-gray-500 truncate">{getSaleTypeLabel(item.sale_type)}</td>
                                                    <td className="px-1.5 py-2.5 align-top text-sm">
                                                        {item.variants && item.variants.length > 0 ? (
                                                            <div className="flex flex-col gap-0.5">
                                                                {item.variants.map(v => {
                                                                    const vm = Number(v.recipe_cost || 0);
                                                                    return (
                                                                        <div key={v.id} className="flex items-baseline gap-2 whitespace-nowrap">
                                                                            <span className="text-[11px] text-gray-400 truncate max-w-[120px]">{v.name}:</span>
                                                                            {vm > 0
                                                                                ? <span className="font-mono font-semibold text-rose-500 text-xs">{vm.toFixed(2)}&nbsp;₼</span>
                                                                                : <span className="text-gray-300 text-xs">—</span>}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : (() => {
                                                            const maya = Number(item.recipe_cost) > 0 ? Number(item.recipe_cost) : Number(item.cost_price || 0);
                                                            return maya > 0
                                                                ? <span className="font-mono font-semibold text-rose-500 whitespace-nowrap">{maya.toFixed(2)}&nbsp;₼</span>
                                                                : <span className="text-gray-300">—</span>;
                                                        })()}
                                                    </td>
                                                    <td className="px-1.5 py-2.5 align-top text-sm">
                                                        {item.variants && item.variants.length > 0 ? (
                                                            <div className="flex flex-col gap-0.5">
                                                                {item.variants.map(v => (
                                                                    <div key={v.id} className="flex items-baseline gap-2 whitespace-nowrap">
                                                                        <span className="text-[11px] text-gray-500 truncate max-w-[120px]">{v.name}:</span>
                                                                        <span className="font-bold font-mono text-primary-600 text-xs">{parseFloat(v.price).toFixed(2)}&nbsp;₼</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span className="font-bold font-mono text-primary-600 whitespace-nowrap">{parseFloat(item.price).toFixed(2)}&nbsp;₼</span>
                                                        )}
                                                    </td>
                                                    <td className="px-1.5 py-2.5 align-top">
                                                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${item.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-500'}`}>
                                                            {item.active ? 'Aktiv' : 'Passiv'}
                                                        </span>
                                                    </td>
                                                    {/* Əməliyyat sütunu sağa yapışıq: cədvəl yanlara sürüşsə də düymələr görünür */}
                                                    <td className="sticky right-0 z-[1] bg-white px-1.5 py-2.5 align-top whitespace-nowrap shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.15)]">
                                                        <div className="flex flex-nowrap gap-0.5 justify-end">
                                                            <button onClick={() => openItemEdit(item)} title="Redaktə et"
                                                                className="w-6 h-6 flex items-center justify-center rounded-lg text-xs bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all">
                                                                ✏️
                                                            </button>
                                                            {canUseRecipes && (
                                                                <button onClick={() => openTexkarta(item)} title="Texkarta"
                                                                    className="w-6 h-6 flex items-center justify-center rounded-lg text-xs bg-primary-50 text-primary-600 hover:bg-primary-100 transition-all">
                                                                    📋
                                                                </button>
                                                            )}
                                                            <button onClick={() => handleItemToggle(item)} title={item.active ? 'Deaktiv et' : 'Aktiv et'}
                                                                className="w-6 h-6 flex items-center justify-center rounded-lg text-xs bg-amber-50 text-amber-600 hover:bg-amber-100 transition-all">
                                                                {item.active ? '🙈' : '👁️'}
                                                            </button>
                                                            <button onClick={() => handleItemDelete(item)} title="Sil"
                                                                className="w-6 h-6 flex items-center justify-center rounded-lg text-xs bg-red-50 text-red-500 hover:bg-red-100 transition-all">
                                                                🗑️
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </>
                        )}
                        </div>
                    </div>
                </div>
            </div>
            )}

            {/* KATEQORİYA MODAL */}
            {catModal && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                     onClick={(e) => e.target === e.currentTarget && setCatModal(false)}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col max-h-[92vh]">
                        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 shrink-0">
                            <h3 className="font-bold text-base text-gray-800">
                                {editCat ? 'Kateqoriyanı redaktə et' : 'Yeni kateqoriya'}
                            </h3>
                            <button type="button" onClick={() => setCatModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <form onSubmit={handleCatSubmit} className="flex flex-1 min-h-0 flex-col">
                        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 flex flex-col gap-4">
                            {/* ── Tablar ── */}
                            <div className="flex gap-1 rounded-xl border border-gray-200 bg-gray-50 p-1 text-xs font-bold">
                                {[
                                    { k: 'main', t: 'Əsas' },
                                    { k: 'printers', t: 'Printerlər' },
                                    { k: 'image', t: 'Şəkil' },
                                ].map(tb => (
                                    <button key={tb.k} type="button" onClick={() => setCatTab(tb.k)}
                                        className={`flex-1 rounded-lg px-2 py-2 transition-all ${catTab === tb.k ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-500 hover:text-primary-600'}`}>
                                        {tb.t}
                                    </button>
                                ))}
                            </div>

                            {/* ═══ TAB: ƏSAS ═══ */}
                            {catTab === 'main' && (<>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Ad</label>
                                <input
                                    type="text"
                                    value={catForm.name}
                                    onChange={e => setCatForm({ ...catForm, name: e.target.value })}
                                    placeholder="Məs: Əsas yeməklər"
                                    className={inputClass}
                                    required autoFocus
                                />
                            </div>


                            {/* Yalnız admin/kassir görsün — ofisiantlar və QR menyu gizli */}
                            <label className="flex items-start gap-2 rounded-xl border border-rose-100 bg-rose-50/40 p-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={Boolean(catForm.staff_only)}
                                    onChange={(e) => setCatForm({ ...catForm, staff_only: e.target.checked })}
                                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-rose-600"
                                />
                                <div className="flex-1">
                                    <div className="text-sm font-bold text-rose-800">🔒 Yalnız admin/kassir görsün</div>
                                    <div className="text-[11px] text-rose-700/80 mt-0.5 leading-snug">
                                        Tick olarsa ofisiant ekranında və QR menyuda gizli olur (məs. "Personal" kateqoriyası). Yalnız admin və kassir (şifrə ilə) vura bilir.
                                    </div>
                                </div>
                            </label>

                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Sıra nömrəsi</label>
                                <input
                                    type="number"
                                    value={catForm.sort_order}
                                    onChange={e => setCatForm({ ...catForm, sort_order: e.target.value })}
                                    className={inputClass}
                                    min="0"
                                />
                            </div>
                            </>)}

                            {/* ═══ TAB: PRINTERLƏR ═══ */}
                            {catTab === 'printers' && (
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                    Printer (mətbəx)
                                </label>
                                {printers.length === 0 ? (
                                    <p className="text-xs text-gray-400">Printer yüklənir...</p>
                                ) : (
                                    <div className="grid grid-cols-2 gap-2">
                                        {/* No printer option */}
                                        <button
                                            type="button"
                                            onClick={() => setCatForm({ ...catForm, printer: 'none' })}
                                            className={`py-3 px-2 rounded-xl border-2 text-center transition-all ${
                                                catForm.printer === 'none' || !catForm.printer
                                                    ? 'border-red-400 bg-red-50 text-red-600'
                                                    : 'border-gray-200 text-gray-400 hover:border-gray-300'
                                            }`}
                                        >
                                            <div className="text-2xl mb-1">🚫</div>
                                            <div className="text-xs font-bold">Printer yoxdur</div>
                                            <div className="text-xs text-gray-400 font-mono mt-0.5">heç biri</div>
                                        </button>
                                        {printers.map(p => (
                                            <button
                                                key={p.name}
                                                type="button"
                                                onClick={() => setCatForm({ ...catForm, printer: p.name })}
                                                className={`py-3 px-2 rounded-xl border-2 text-center transition-all ${
                                                    catForm.printer === p.name
                                                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                                                        : 'border-gray-200 text-gray-500 hover:border-gray-300'
                                                }`}
                                            >
                                                <div className="text-2xl mb-1">{getPrinterIcon(p.name)}</div>
                                                <div className="text-xs font-bold">{p.label}</div>
                                                <div className="text-xs text-gray-400 font-mono mt-0.5">{p.name}</div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            )}

                            {/* ═══ TAB: ŞƏKIL (yeni + redaktə) ═══ */}
                            {catTab === 'image' && (
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                        Kateqoriya Şəkli (1:1)
                                    </label>
                                    {(() => {
                                        const serverUrl = editCat?.image_url ? `${getAssetBaseUrl()}${editCat.image_url}` : null;
                                        const previewUrl = serverUrl || pendingCatImagePreview;
                                        return previewUrl ? (
                                            <div className="flex items-center gap-3">
                                                <img src={previewUrl} alt="preview"
                                                    className="w-16 h-16 rounded-xl object-cover border-2 border-gray-200" />
                                                <div className="flex flex-col gap-1.5">
                                                    <button type="button" onClick={() => imageInputRef.current?.click()} disabled={imageUploading}
                                                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary-50 text-primary-600 hover:bg-primary-100 disabled:opacity-50">
                                                        {imageUploading ? 'Yüklənir...' : 'Dəyişdir'}
                                                    </button>
                                                    <button type="button"
                                                        onClick={() => { if (editCat?.image_url) handleImageDelete(); else clearPendingCatImage(); }}
                                                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-500 hover:bg-red-100">
                                                        Şəkli sil
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button type="button" onClick={() => imageInputRef.current?.click()} disabled={imageUploading}
                                                className="w-full h-20 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-1 hover:border-primary-400 hover:bg-primary-50 transition-all text-gray-400 hover:text-primary-500 disabled:opacity-50">
                                                <span className="text-2xl">{imageUploading ? '⏳' : '📷'}</span>
                                                <span className="text-xs font-semibold">{imageUploading ? 'Yüklənir...' : 'Şəkil yüklə'}</span>
                                            </button>
                                        );
                                    })()}
                                    <input
                                        ref={imageInputRef}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={handleImageUpload}
                                    />
                                    {/* İnternetdən şəkil tap (kateqoriya adına görə) */}
                                    <div className="mt-2">
                                        <div className="flex gap-2">
                                            <input type="text" value={imgQueryEdit}
                                                onChange={(e) => { setImgQueryEdit(e.target.value); setImgQueryDirty(true); }}
                                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchCategoryImage(); } }}
                                                placeholder="Axtarış sözü (məs: cold drink coffee)"
                                                className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-xs focus:border-primary-400 focus:outline-none" />
                                            <button type="button" onClick={searchCategoryImage} disabled={imgSearching || !imgQueryEdit.trim()}
                                                className="shrink-0 rounded-lg border border-gray-200 px-3 py-2 text-xs font-bold text-gray-600 transition-all hover:bg-gray-50 disabled:opacity-50">
                                                {imgSearching ? '⏳' : '🔍 Tap'}
                                            </button>
                                        </div>
                                        <p className="mt-1 text-[10px] text-gray-400">Kateqoriya adından avtomatik yığılır — istəsən dəyiş.</p>
                                        {imgResults && imgResults.length > 0 && (
                                            <div className="mt-2 grid grid-cols-4 gap-2 rounded-lg border border-gray-100 bg-gray-50 p-2">
                                                {imgResults.map((r, i) => (
                                                    <button key={i} type="button" onClick={() => pickCategoryImage(r.url)} title={r.title}
                                                        className="aspect-square overflow-hidden rounded-lg border border-gray-200 bg-white transition-all hover:ring-2 hover:ring-primary-400">
                                                        <img src={r.thumbnail} alt={r.title} loading="lazy" className="h-full w-full object-cover" />
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        {imgResults && imgResults.length === 0 && (
                                            <p className="mt-1 text-[11px] text-gray-400">Şəkil tapılmadı — adı dəyişib yenidən yoxlayın.</p>
                                        )}
                                        {renderImgPager()}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-3 border-t border-gray-100 px-6 py-4 shrink-0">
                            <button type="button" onClick={() => setCatModal(false)}
                                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50">
                                Ləğv et
                            </button>
                            <button type="submit"
                                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white"
                                style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}>
                                {editCat ? 'Yadda saxla' : 'Əlavə et'}
                            </button>
                        </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MƏHSUL MODAL */}
            {itemModal && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                     onClick={(e) => e.target === e.currentTarget && setItemModal(false)}>
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl overflow-y-auto max-h-[90vh]">
                        <h3 className="font-bold text-lg mb-5 text-gray-800">
                            {editItem ? 'Məhsulu redaktə et' : 'Yeni məhsul'}
                        </h3>
                        <form onSubmit={handleItemSubmit} className="flex flex-col gap-4">
                            {/* ── Tablar ── */}
                            <div className="flex gap-1 rounded-xl border border-gray-200 bg-gray-50 p-1 text-xs font-bold">
                                {[
                                    { k: 'main', t: 'Əsas' },
                                    { k: 'image', t: 'Şəkil' },
                                    { k: 'variants', t: 'Variantlar' },
                                    { k: 'access', t: 'İcazələr' },
                                ].map(tb => (
                                    <button key={tb.k} type="button" onClick={() => setItemTab(tb.k)}
                                        className={`flex-1 rounded-lg px-2 py-2 transition-all ${itemTab === tb.k ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-500 hover:text-primary-600'}`}>
                                        {tb.t}
                                    </button>
                                ))}
                            </div>

                            {/* ═══ TAB: ƏSAS ═══ */}
                            {itemTab === 'main' && (<>
                            <div className="relative">
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Kateqoriya</label>
                                <input
                                    type="text"
                                    value={catSearch}
                                    onChange={e => { setCatSearch(e.target.value); setCatOpen(true); }}
                                    onFocus={() => setCatOpen(true)}
                                    onBlur={() => setTimeout(() => setCatOpen(false), 150)}
                                    placeholder="Kateqoriya axtar / seç..."
                                    className={inputClass}
                                    required={!itemForm.category_id}
                                />
                                {catOpen && (
                                    <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg">
                                        {categories.filter(c => c.active && (c.name || '').toLowerCase().includes(catSearch.toLowerCase())).map(cat => {
                                            const printer = getPrinterInfo(cat.printer);
                                            return (
                                                <button key={cat.id} type="button"
                                                    onMouseDown={(e) => { e.preventDefault(); setItemForm({ ...itemForm, category_id: cat.id }); setCatSearch(cat.name); setCatOpen(false); }}
                                                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-primary-50 ${String(itemForm.category_id) === String(cat.id) ? 'bg-primary-50 font-bold text-primary-600' : 'text-gray-700'}`}>
                                                    <span>{printer.icon}</span><span>{cat.name}</span>
                                                </button>
                                            );
                                        })}
                                        {categories.filter(c => c.active && (c.name || '').toLowerCase().includes(catSearch.toLowerCase())).length === 0 && (
                                            <div className="px-3 py-2 text-xs text-gray-400">Kateqoriya tapılmadı</div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Ad</label>
                                <input
                                    type="text"
                                    name="item-name"
                                    value={itemForm.name}
                                    onChange={e => setItemForm({ ...itemForm, name: e.target.value })}
                                    placeholder="Məs: Qara çay"
                                    className={inputClass}
                                    required autoFocus
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Təsvir (isteğe bağlı)</label>
                                <input
                                    type="text"
                                    value={itemForm.description}
                                    onChange={e => setItemForm({ ...itemForm, description: e.target.value })}
                                    placeholder="Qısa açıqlama"
                                    className={inputClass}
                                />
                            </div>

                            {/* Maya (alış) dəyəri */}
                            {Number(editItem?.recipe_cost || 0) > 0 ? (
                                // Texkarta varsa → resept üzrə avtomatik, dəyişilməz (disabled)
                                <div>
                                    <label className="block text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-1.5">💰 Maya dəyəri (texkarta üzrə)</label>
                                    <input
                                        type="text"
                                        value={`${Number(editItem.recipe_cost).toFixed(2)} ₼`}
                                        disabled
                                        className={inputClass + ' bg-gray-100 text-gray-600 cursor-not-allowed'}
                                    />
                                    <p className="text-[11px] text-gray-400 mt-1">Texkartadakı ingredientlərdən avtomatik hesablanır (miqdar × vahid qiymət).</p>
                                </div>
                            ) : manualCost ? (
                                // Manual rejim → əl ilə yazılır
                                <div>
                                    <label className="block text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-1.5">💰 Maya (alış) dəyəri (₼)</label>
                                    <input
                                        type="number" step="0.01" min="0"
                                        value={itemForm.cost_price}
                                        onChange={e => setItemForm({ ...itemForm, cost_price: e.target.value })}
                                        placeholder="Məs: 1.72"
                                        className={inputClass}
                                    />
                                    <p className="text-[11px] text-gray-400 mt-1">Gəlir hesabatında mənfəət hesablanması üçün.</p>
                                </div>
                            ) : null}

                            <div className={`grid gap-3 ${variants.length > 0 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                                    {variants.length === 0 ? (
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{itemForm.is_hourly ? 'Tarif (₼/saat)' : 'Qiymət (₼)'}</label>
                                            <input
                                                type="number"
                                                value={itemForm.price}
                                                onChange={e => setItemForm({ ...itemForm, price: e.target.value })}
                                                placeholder="0.00"
                                                step="0.01" min="0"
                                                className={inputClass}
                                                required
                                            />
                                        </div>
                                    ) : (
                                        <div className="rounded-xl border border-dashed border-primary-200 bg-primary-50 px-3 py-2.5 text-xs font-semibold text-primary-600">
                                            💡 Qiymət variantlar üzrə müəyyənləşir — aşağıda hər variant üçün ayrıca qiymət yazın
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Satış növü</label>
                                        <select
                                            value={itemForm.sale_type}
                                            onChange={e => setItemForm({ ...itemForm, sale_type: e.target.value })}
                                            className={inputClass}
                                        >
                                            <option value="piece">Ədəd ilə</option>
                                            <option value="weight">Çəki ilə (kq)</option>
                                            <option value="liter">Litr ilə (lt)</option>
                                        </select>
                                    </div>
                                    {hourlyEnabled && (
                                        <div className="col-span-2">
                                            <label className="flex cursor-pointer items-center justify-between gap-2 rounded-xl border-2 border-indigo-200 bg-indigo-50/40 px-3 py-2.5 text-sm font-semibold text-indigo-900">
                                                <span className="flex items-center gap-1.5"><span>⏱️</span><span>Saatlıq (tarif) — qiymət = AZN/saat</span></span>
                                                <input type="checkbox" checked={Boolean(itemForm.is_hourly)}
                                                    onChange={(e) => setItemForm({ ...itemForm, is_hourly: e.target.checked })}
                                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600" />
                                            </label>
                                            {itemForm.is_hourly && (
                                                <p className="mt-1 text-[10px] text-indigo-700/70">
                                                    Sifarişə əlavə olunanda sayğac başlayır; hesab keçən vaxta görə hesablanır (məs. 10 ₼/saat → 2 saat 30 dəq = 25 ₼).
                                                </p>
                                            )}
                                        </div>
                                    )}
                                    {/* QR porsiya — yalnız çəki/litr məhsulları üçün */}
                                    {(itemForm.sale_type === 'weight' || itemForm.sale_type === 'liter') && (() => {
                                        const unit = itemForm.sale_type === 'liter' ? 'ml' : 'q';
                                        const grams = parseInt(itemForm.qr_portion_grams, 10) || 0;
                                        const perUnit = parseFloat(itemForm.price) || 0;
                                        const portionPrice = grams > 0 ? (perUnit * grams / 1000) : 0;
                                        return (
                                            <div className="rounded-xl border border-teal-100 bg-teal-50/40 p-3">
                                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">QR menyuda porsiya kimi göstər</label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="number" min="0" step="50"
                                                        value={itemForm.qr_portion_grams}
                                                        onChange={e => setItemForm({ ...itemForm, qr_portion_grams: e.target.value })}
                                                        placeholder="məs. 500"
                                                        className={`${inputClass} w-32`}
                                                    />
                                                    <span className="text-sm font-semibold text-gray-600">{itemForm.sale_type === 'liter' ? 'ml başına' : 'qram başına'}</span>
                                                </div>
                                                {grams > 0 ? (
                                                    <p className="mt-2 text-[11px] font-bold text-teal-700">QR-də görünəcək: {grams} {unit} — {portionPrice.toFixed(2)} ₼</p>
                                                ) : (
                                                    <p className="mt-2 text-[11px] text-gray-400 leading-snug">Boş / 0 → QR-də adi {itemForm.sale_type === 'liter' ? 'litr' : 'kq'} qiyməti göstərilir. Kassa yenə çəkiylə hesablayır.</p>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    {/* 1 porsiyada neçə ədəd — banket mayası üçün (məs. tikə: 1 pors = 3 ədəd) */}
                                    <div className="rounded-xl border border-amber-100 bg-amber-50/40 p-3">
                                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">1 porsiyada neçə ədəd</label>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="number" min="1" step="1"
                                                value={itemForm.portion_pieces ?? 1}
                                                onChange={e => setItemForm({ ...itemForm, portion_pieces: e.target.value })}
                                                placeholder="1"
                                                className={`${inputClass} w-32`}
                                            />
                                            <span className="text-sm font-semibold text-gray-600">ədəd / porsiya</span>
                                        </div>
                                        <p className="mt-2 text-[11px] leading-snug text-gray-400">
                                            Məs. 1 porsiya tikə = 3 ədəd → <b>3</b> yaz. Banket mayasında 1 ədədin dəyəri (porsiya mayası ÷ ədəd) hesablanır. <b>1</b> = adi.
                                        </p>
                                    </div>
                                </div>
                            </>)}

                            {/* ═══ TAB: İCAZƏLƏR VƏ PRINTERLƏR ═══ */}
                            {itemTab === 'access' && (<>
                            {/* Yenilik tick — QR menyu və ofisiant ekranında "Yenilikler" kateqoriyasında göstərir */}
                            <label className="flex items-start gap-2 rounded-xl border border-amber-100 bg-amber-50/40 p-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={Boolean(itemForm.is_new)}
                                    onChange={(e) => setItemForm({ ...itemForm, is_new: e.target.checked })}
                                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-amber-600"
                                />
                                <div className="flex-1">
                                    <div className="text-sm font-bold text-amber-800">✨ Yenilik</div>
                                    <div className="text-[11px] text-amber-700/80 mt-0.5 leading-snug">
                                        Bu məhsul QR menyuda və ofisiant ekranında üstdə yer alan "✨ Yeniliklər" kateqoriyasında görsənir.
                                    </div>
                                </div>
                            </label>
                            {/* Staff-only tick — QR menyuda gizli + ofisiant (waiter) vura bilməz; admin/kassir vurur */}
                            <label className="flex items-start gap-2 rounded-xl border border-sky-100 bg-sky-50/40 p-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={Boolean(itemForm.staff_only)}
                                    onChange={(e) => setItemForm({ ...itemForm, staff_only: e.target.checked })}
                                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-sky-600"
                                />
                                <div className="flex-1">
                                    <div className="text-sm font-bold text-sky-800">🔒 Yalnız admin/kassir</div>
                                    <div className="text-[11px] text-sky-700/80 mt-0.5 leading-snug">
                                        Bu məhsul QR menyuda görsənmir və ofisiant (waiter) onu vura bilmir. Yalnız admin/kassir sifarişə əlavə edə bilər. (Tam deaktiv deyil.)
                                    </div>
                                </div>
                            </label>
                            {/* Yalnız set/banket içində — menyuda heç kəsə görünmür, tək vurula bilmir */}
                            <label className="flex items-start gap-2 rounded-xl border border-violet-100 bg-violet-50/40 p-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={Boolean(itemForm.set_only)}
                                    onChange={(e) => setItemForm({ ...itemForm, set_only: e.target.checked })}
                                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-violet-600"
                                />
                                <div className="flex-1">
                                    <div className="text-sm font-bold text-violet-800">🧩 Yalnız set/banket içində</div>
                                    <div className="text-[11px] text-violet-700/80 mt-0.5 leading-snug">
                                        Bu məhsul QR menyuda və sifariş ekranında heç kəsə görünmür (admin, kassir, ofisiant). Tək vurula bilmir, yalnız set və banketlərin içində işlədilir.
                                    </div>
                                </div>
                            </label>
                            {/* Ağıllı banket üçün məhsul tipi */}
                            <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3">
                                <div className="text-sm font-bold text-emerald-800">✨ Banketdə necə paylanır</div>
                                <div className="text-[11px] text-emerald-700/80 mt-0.5 mb-2 leading-snug">
                                    Ağıllı banket yaradanda bu məhsulun miqdarı buna görə hesablanır. «Avtomatik» kateqoriya və porsiyaya görə seçir.
                                </div>
                                <select value={itemForm.banquet_role || ''}
                                    onChange={(e) => setItemForm({ ...itemForm, banquet_role: e.target.value })}
                                    className="w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm text-gray-800 focus:border-emerald-400 focus:outline-none">
                                    <option value="">Avtomatik</option>
                                    <option value="individual">Hər qonağa 1 ədəd (şiş, bilinçik)</option>
                                    <option value="shared">Masaya, hər 6 nəfərə (salat, turşu, içki)</option>
                                    <option value="whole">Bütöv yemək, hər 6 nəfərə (sac, tabaka)</option>
                                    <option value="exclude">Banketə qoyulmasın</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Sıra №</label>
                                <input
                                    type="number"
                                    value={itemForm.sort_order}
                                    onChange={e => setItemForm({ ...itemForm, sort_order: e.target.value })}
                                    className={inputClass}
                                    min="0"
                                />
                            </div>
                            {/* Per-item printer override */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                    Printer (xüsusi override)
                                </label>
                                <select
                                    value={itemForm.printer || ''}
                                    onChange={e => setItemForm({ ...itemForm, printer: e.target.value || null })}
                                    className={inputClass}
                                >
                                    {(() => {
                                        // Default = kateqoriyanın printeri — hansı olduğunu göstər
                                        const cat = categories.find(c => String(c.id) === String(itemForm.category_id));
                                        const cp = cat?.printer;
                                        let d;
                                        if (cp === 'none') d = 'çap yoxdur';
                                        else if (!cp) d = 'təyin olunmayıb';
                                        else { const info = getPrinterInfo(cp); d = `${info.label} · ${info.name}`; }
                                        return <option value="">— Kateqoriya printeri: {d} (default) —</option>;
                                    })()}
                                    <option value="none">🚫 Printer yoxdur (heç yerə çap etmə)</option>
                                    {printers.map(p => (
                                        <option key={p.name} value={p.name}>
                                            {getPrinterIcon(p.name)} {p.label} ({p.name})
                                        </option>
                                    ))}
                                </select>
                                {itemForm.printer === 'none' && (
                                    <p className="text-[11px] text-red-500 mt-1">
                                        Bu məhsul heç bir printerə çap olunmayacaq.
                                    </p>
                                )}
                                {itemForm.printer && itemForm.printer !== 'none' && (
                                    <p className="text-[11px] text-blue-500 mt-1">
                                        Bu məhsul kateqoriya printerini deyil, <strong>{itemForm.printer}</strong> printerini istifadə edəcək.
                                    </p>
                                )}
                            </div>
                            </>)}

                            {/* ═══ TAB: ŞƏKIL ═══ */}
                            {itemTab === 'image' && (<>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                    Məhsul şəkli
                                </label>
                                {editItem ? (
                                    editItem.image_url ? (
                                        <div className="flex items-center gap-3">
                                            <img
                                                src={`${getAssetBaseUrl()}${editItem.image_url}`}
                                                alt="preview"
                                                draggable={false}
                                                onContextMenu={(e) => e.preventDefault()}
                                                style={{ WebkitTouchCallout: 'none' }}
                                                className="w-16 h-16 rounded-xl object-cover border-2 border-gray-200 select-none"
                                            />
                                            <div className="flex flex-col gap-1.5">
                                                <button
                                                    type="button"
                                                    onClick={() => itemImageInputRef.current?.click()}
                                                    disabled={itemImageUploading}
                                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary-50 text-primary-600 hover:bg-primary-100 disabled:opacity-50"
                                                >
                                                    {itemImageUploading ? 'Yüklənir...' : 'Dəyişdir'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleItemImageDownload}
                                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                                                >
                                                    ⬇️ Endir
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleItemImageDelete}
                                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-500 hover:bg-red-100"
                                                >
                                                    Şəkli sil
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => itemImageInputRef.current?.click()}
                                            disabled={itemImageUploading}
                                            className="w-full h-20 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-1 hover:border-primary-400 hover:bg-primary-50 transition-all text-gray-400 hover:text-primary-500 disabled:opacity-50"
                                        >
                                            <span className="text-2xl">{itemImageUploading ? '⏳' : '🖼️'}</span>
                                            <span className="text-xs font-semibold">
                                                {itemImageUploading ? 'Yüklənir...' : 'Şəkil yüklə'}
                                            </span>
                                        </button>
                                    )
                                ) : (
                                    pendingItemImagePreview ? (
                                        <div className="flex items-center gap-3">
                                            <img
                                                src={pendingItemImagePreview}
                                                alt="preview"
                                                className="w-16 h-16 rounded-xl object-cover border-2 border-gray-200"
                                            />
                                            <div className="flex flex-col gap-1.5">
                                                <button
                                                    type="button"
                                                    onClick={() => newItemImageInputRef.current?.click()}
                                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary-50 text-primary-600 hover:bg-primary-100"
                                                >
                                                    Dəyişdir
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        if (pendingItemImagePreview.startsWith('blob:')) URL.revokeObjectURL(pendingItemImagePreview);
                                                        setPendingItemImage(null);
                                                        setPendingImageUrl(null);
                                                        setPendingItemImagePreview(null);
                                                    }}
                                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-500 hover:bg-red-100"
                                                >
                                                    Şəkli sil
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => newItemImageInputRef.current?.click()}
                                            className="w-full h-20 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-1 hover:border-primary-400 hover:bg-primary-50 transition-all text-gray-400 hover:text-primary-500"
                                        >
                                            <span className="text-2xl">🖼️</span>
                                            <span className="text-xs font-semibold">Şəkil yüklə</span>
                                        </button>
                                    )
                                )}
                                <input
                                    ref={itemImageInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={handleItemImageUpload}
                                />
                                <input
                                    ref={newItemImageInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={handlePendingItemImageSelect}
                                />
                            </div>

                            {/* İnternetdən şəkil tap (ad + təsvirə görə) */}
                            {canUseMenuImages && (
                                <div className="mt-1">
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">İnternetdən şəkil tap</label>
                                    <div className="flex gap-2">
                                        <input type="text" value={imgQueryEdit}
                                            onChange={(e) => { setImgQueryEdit(e.target.value); setImgQueryDirty(true); }}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchProductImage(); } }}
                                            placeholder="Axtarış sözü (məs: apple kiwi smoothie)"
                                            className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-xs focus:border-primary-400 focus:outline-none" />
                                        <button type="button" onClick={searchProductImage} disabled={imgSearching || !imgQueryEdit.trim()}
                                            className="shrink-0 rounded-lg border border-gray-200 px-3 py-2 text-xs font-bold text-gray-600 transition-all hover:bg-gray-50 disabled:opacity-50">
                                            {imgSearching ? '⏳' : '🔍 Tap'}
                                        </button>
                                    </div>
                                    <p className="mt-1 text-[10px] text-gray-400">Ad + təsvirdən avtomatik yığılır — istəsən dəyiş.</p>
                                    {imgResults && imgResults.length > 0 && (
                                        <div className="mt-2 grid grid-cols-4 gap-2 rounded-lg border border-gray-100 bg-gray-50 p-2">
                                            {imgResults.map((r, i) => (
                                                <button key={i} type="button" onClick={() => pickProductImage(r.url)}
                                                    title={r.title}
                                                    className="aspect-square overflow-hidden rounded-lg border border-gray-200 bg-white transition-all hover:ring-2 hover:ring-primary-400">
                                                    <img src={r.thumbnail} alt={r.title} loading="lazy"
                                                        className="h-full w-full object-cover" />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {imgResults && imgResults.length === 0 && (
                                        <p className="mt-1 text-[11px] text-gray-400">Şəkil tapılmadı — adı dəyişib yenidən yoxlayın.</p>
                                    )}
                                    {renderImgPager()}
                                </div>
                            )}
                            </>)}

                            {/* ═══ TAB: VARIANTLAR ═══ */}
                            {itemTab === 'variants' && (
                                <div className="border border-gray-200 rounded-xl p-3.5">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                                        Variantlar (ölçü / seçim)
                                    </p>
                                    {!editItem && (
                                        <p className="mb-3 rounded-lg bg-blue-50 px-3 py-2 text-[11px] font-medium text-blue-600">
                                            💡 Variantları indi əlavə et — məhsul yadda saxlananda birlikdə yaradılacaq.
                                        </p>
                                    )}

                                    {/* Existing variants list */}
                                    {variants.length > 0 && (
                                        <div className="flex flex-col gap-1.5 mb-3">
                                            {variants.map((v, idx) => (
                                                <div
                                                    key={v.id}
                                                    onDragOver={(e) => e.preventDefault()}
                                                    onDrop={() => { reorderVariants(variantDragIndex, idx); setVariantDragIndex(null); }}
                                                    className={`border border-gray-100 rounded-xl overflow-hidden ${variantDragIndex === idx ? 'opacity-50' : ''}`}
                                                >
                                                    <div className="flex items-center justify-between bg-gray-50 px-2 py-2 gap-1.5">
                                                        {/* Sürüşdürmə tutacağı */}
                                                        <span
                                                            draggable
                                                            onDragStart={() => setVariantDragIndex(idx)}
                                                            onDragEnd={() => setVariantDragIndex(null)}
                                                            title="Sürüşdürüb sıranı dəyiş"
                                                            className="cursor-grab active:cursor-grabbing select-none text-gray-400 hover:text-gray-600 px-0.5 text-base leading-none"
                                                        >⠿</span>
                                                        <input
                                                            type="text"
                                                            value={v.name}
                                                            onChange={(e) => updateVariantLocal(v.id, { name: e.target.value })}
                                                            onBlur={() => saveVariantEdit(v)}
                                                            className="flex-1 min-w-0 px-2 py-1 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                                                        />
                                                        <input
                                                            type="number" step="0.01" min="0"
                                                            value={v.price}
                                                            onChange={(e) => updateVariantLocal(v.id, { price: e.target.value })}
                                                            onBlur={() => saveVariantEdit(v)}
                                                            className="w-20 shrink-0 px-2 py-1 rounded-lg border border-gray-200 text-sm text-right font-bold text-primary-600 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                                                        />
                                                        {/* Printer selector */}
                                                        {variantPrinterEdit === v.id ? (
                                                            <div className="flex items-center gap-1">
                                                                <select
                                                                    autoFocus
                                                                    defaultValue={v.printer || ''}
                                                                    onChange={e => saveVariantPrinter(v.id, e.target.value)}
                                                                    className="text-xs border border-blue-300 rounded-lg px-1.5 py-1 focus:outline-none bg-white"
                                                                >
                                                                    <option value="">— Default —</option>
                                                                    {printers.map(p => (
                                                                        <option key={p.name} value={p.name}>
                                                                            {p.label} ({p.name})
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                <button type="button" onClick={() => setVariantPrinterEdit(null)}
                                                                    className="text-gray-400 hover:text-gray-600 text-xs px-1">✕</button>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={() => setVariantPrinterEdit(v.id)}
                                                                className={`text-xs px-2 py-1 rounded-lg font-semibold transition-all border shrink-0 ${
                                                                    v.printer
                                                                        ? 'bg-blue-50 text-blue-600 border-blue-200'
                                                                        : 'bg-white text-gray-400 border-gray-200 hover:bg-gray-50'
                                                                }`}
                                                            >
                                                                🖨️ {v.printer || 'Printer'}
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => deleteVariant(v.id)}
                                                            disabled={variantLoading}
                                                            className="w-6 h-6 flex items-center justify-center rounded-full text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-40 shrink-0"
                                                        >✕</button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Add new variant */}
                                    <div className="flex gap-2 items-center">
                                        <input
                                            ref={variantNameRef}
                                            type="text"
                                            value={variantForm.name}
                                            onChange={e => setVariantForm({ ...variantForm, name: e.target.value })}
                                            placeholder="Kiçik / Orta / Böyük..."
                                            className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); variantPriceRef.current?.focus(); } }}
                                        />
                                        <input
                                            ref={variantPriceRef}
                                            type="number"
                                            value={variantForm.price}
                                            onChange={e => setVariantForm({ ...variantForm, price: e.target.value })}
                                            placeholder="₼"
                                            step="0.01" min="0"
                                            className="w-20 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addVariant(); } }}
                                        />
                                        <button
                                            type="button"
                                            onClick={addVariant}
                                            disabled={variantLoading || !variantForm.name.trim() || variantForm.price === ''}
                                            className="px-3 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-40 transition-opacity"
                                            style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                                        >
                                            +
                                        </button>
                                    </div>
                                    {variants.length === 0 && (
                                        <p className="text-xs text-gray-400 mt-2">
                                            Variant yoxdur — əlavə etmək üçün ad və qiymət yazın.
                                        </p>
                                    )}
                                </div>
                            )}

                            <div className="flex gap-3 mt-2">
                                <button type="button" onClick={() => setItemModal(false)}
                                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50">
                                    {editItem ? 'Ləğv et' : '✕ Bağla'}
                                </button>
                                <button type="submit"
                                    className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white"
                                    style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}>
                                    {editItem ? 'Yadda saxla' : '➕ Əlavə et (davam)'}
                                </button>
                            </div>
                            {!editItem && (
                                <p className="text-[11px] text-gray-400 text-center -mt-1">
                                    💡 "Əlavə et" basdıqda forma sıfırlanır, ardıcıl məhsul əlavə edə bilərsiniz
                                </p>
                            )}
                        </form>
                    </div>
                </div>
            )}

            {/* MOBİL: Kateqoriya bottom-sheet (desktopda sidebar) */}
            {catSheetOpen && (
                <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 lg:hidden"
                     onClick={(e) => e.target === e.currentTarget && setCatSheetOpen(false)}>
                    <div className="max-h-[80vh] w-full overflow-y-auto rounded-t-2xl bg-white p-3 shadow-2xl">
                        <div className="mb-2 flex items-center justify-between px-1">
                            <h3 className="text-sm font-bold text-gray-700">📂 Kateqoriyalar</h3>
                            <button type="button" onClick={() => setCatSheetOpen(false)}
                                className="rounded-lg px-2 py-1 text-sm font-semibold text-gray-400 hover:bg-gray-50">✕ Bağla</button>
                        </div>
                        {/* "Hamısı" seçimi */}
                        <button type="button"
                            onClick={() => { setActiveCat(null); setCatSheetOpen(false); }}
                            className={`mb-1.5 flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm font-semibold transition-all ${!activeCat ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-50 text-gray-600'}`}>
                            <span>🍽️ Hamısı</span>
                            <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">{items.length}</span>
                        </button>
                        {categoriesPanel}
                    </div>
                </div>
            )}
        </>
    );

    return embedded ? content : <Layout>{content}</Layout>;
};

export default Menu;
